import {
  ALL_GROUP_CATEGORY_IDS,
  APOTEA_COSMETIC_CATEGORY_IDS,
  APOTEA_SUPPLEMENT_CATEGORY_IDS,
  GROUP_CATEGORY_IDS,
} from './groupCategoryIds';
import IProduct, { IFoodlaCategory } from 'models/product';
import type { IProductExcel } from '../types';
import type { ProductFieldGroup } from 'components/product/ProductUpdateForm/utils/productFieldVisibility';
import { ReactNode } from 'react';
import { getProductMarkings } from 'components/product/ProductUpdateForm/fields/labels';
import countries from 'constants/countries';
import {
  additionalCharacteristics,
  animalCategoriesForFeed,
  animalFeedTypes,
  APOTEA_LABEL_NAME,
  catchArea,
  categoryWeightloss,
  fishCatchMethod,
  fishProductionMethod,
  preparedForm,
  recyclingType,
} from 'components/constants-ts';
import { isBlank } from 'utils/helpers-ts';
import { convertStringToBoolean } from '../utils/excel';
import { isNewNonFood } from 'components/product/ProductUpdateForm/fields/category';
import { IFoodlaCategoryOption } from 'models/category';

type TCheckMergeParams = {
  excelTitle: string;
  foodlaCategory?: IFoodlaCategory;
  rootCategory: IFoodlaCategoryOption;
};
type TCheckMergeFn = (params: TCheckMergeParams) => boolean;

type TSplitMergeColumnValueParams = {
  excelTitle: string;
  excelValue: unknown;
  oldValue?: unknown;
  foodlaCategory?: IFoodlaCategory;
  rootCategory: IFoodlaCategoryOption;
};
type TSplitMergeValueFn = (params: TSplitMergeColumnValueParams) => { value: unknown; warnings?: string[] };

type TMergeColumns = {
  isColumnToMerge: TCheckMergeFn;
  mergeValue: TSplitMergeValueFn;
};

type TSplitFromColumn = {
  isColumnToSplit: TCheckMergeFn;
  splitValue: TSplitMergeValueFn;
};

const renderSuggestionList = (list: { displayName: string }[], options?: { notTitle?: boolean }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {!options?.notTitle && <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Acceptabla värden</div>}
      {list.map(({ displayName }, index) => (
        <div key={index}>{displayName}</div>
      ))}
    </div>
  );
};

const labelsCheckMergeColumnFn: Record<'vegan' | 'glutenFree' | 'apotea', TCheckMergeFn> = {
  vegan: ({ excelTitle }) =>
    [
      'Har produkten en vegan-certifiering och/eller är vegansk?',
      'Har produkten vegan-certifiering / är vegansk?',
    ].includes(excelTitle),
  glutenFree: ({ excelTitle }) => ['Är produkten glutenfri?'].includes(excelTitle),
  apotea: ({ excelTitle }) => /^Hållbarhetsmärkning\s*[1-5]$/i.test(excelTitle),
};

const dosageAndUsageInstructionsCheckMergeColumnFn: Record<'dosering' | 'warning', TCheckMergeFn> = {
  dosering: ({ excelTitle }) =>
    [
      'Användningsinstruktioner och dosering',
      'Användningsinstruktioner, inklusive eventuella försiktighetsåtgärder',
      'Dosering och användningsinstruktioner',
      'Användningsinstruktioner',
      'Dosering och användsinstruktioner',
      'Dosering & användning',
    ].includes(excelTitle),
  warning: ({ excelTitle }) =>
    [
      'Eventuella försiktighetsåtgärder, signalord och faroangivelse',
      'Eventuella försiktighetsåtgärder, signalord och faroangivelse.',
    ].includes(excelTitle),
};

export type TColumnDetail = {
  title: string;
  subTitle?: string;
  field: keyof IProduct;
  previewOrder?: number;
  previewGroup?: { id: ProductFieldGroup; label: string };
  previewTitle?: string;
  fieldMappingName?: string;
  isExtra?: boolean;
  useDefaultTitle?: boolean;
  extraCorrespondingField?: keyof IProduct;
  searchTitleList?: string[];
  showInCategories: string[];
  mandatoryInCategories: string[];
  defaultValue?: string;
  mandatoryDefaultValue?: string;
  suggestions?: ReactNode;
  rowRuntimeFlags?: IProductExcel['_runtimeFlags'];
  mergeColumns?: TMergeColumns;
  splitFromColumn?: TSplitFromColumn;
  unit?: string;
};

export const COLUMN_DETAILS: TColumnDetail[] = [
  {
    title: 'Kategori',
    field: 'foodlaCategory',
    previewOrder: 1,
    previewGroup: { id: 'category', label: 'Kategori' },
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Produktnamn',
    field: 'title',
    previewOrder: 7,
    previewGroup: { id: 'identity', label: 'Produktinformation' },
    useDefaultTitle: true,
    searchTitleList: [
      'Benämning',
      'Produktnamn',
      'Artikelbenämning',
      'Artikelnamn',
      'Livsmedlets beteckning (produktnamn)',
      'Produktnamn/beteckning',
      'Beteckning',
      'Produkt beteckning',
      'Produktnamn som visas på site',
    ],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Produktdokument (länk)',
    field: 'documentUrls',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'EAN-kod (13 eller 8 siffror)',
    field: 'EAN',
    previewOrder: 8,
    previewGroup: { id: 'identity', label: 'Produktinformation' },
    fieldMappingName: 'EAN-kod (13 eller 8 siffror)',
    searchTitleList: [
      'På konsumentförpackningen angiven EAN-kod (13 eller 8 siffror)',
      'EAN-kod konsumentförpackning\nand\nGTIN',
      'EAN-kod (13 eller 8 siffori)',
      'EAN-kod',
      'GTIN',
      'EAN',
      'EAN-kod konsumentförpackning (8 eller 13)',
      'EAN-kod konsumentförpackning (8 eller 13 siffror)',
    ],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Bilder',
    field: 'image_src',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Leverantörens artikelnummer',
    field: 'purchasingDataSupplierArticleNo',
    previewOrder: 9,
    previewGroup: { id: 'identity', label: 'Produktinformation' },
    useDefaultTitle: true,
    searchTitleList: ['Artnr'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Produktbeskrivning',
    field: 'short_text',
    previewOrder: 11,
    previewGroup: { id: 'identity', label: 'Produktinformation' },
    useDefaultTitle: true,
    searchTitleList: ['Produktbeskrivning till sajt, unik för Apotea', 'Beskriv produkten'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Djurfodertyp - "Helfoder (Foderblandning)", "Kompletteringsfoder (Foderblandning)", "Foderråvara"',
    field: 'animalFeedType',
    previewOrder: 13,
    previewGroup: { id: 'contents', label: 'Innehåll' },
    previewTitle: 'Djurfodertyp',
    fieldMappingName: 'Djurfodertyp',
    showInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
    mandatoryInCategories: [],
    suggestions: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Acceptabla värden</div>
        {renderSuggestionList(animalFeedTypes, { notTitle: true })}
      </div>
    ),
  },
  {
    title: 'Skriv en kort säljande beskrivning av produkten (max 155 tecken)',
    field: 'shortSalesDescription',
    previewOrder: 12,
    previewGroup: { id: 'identity', label: 'Produktinformation' },
    previewTitle: 'Kort säljande beskrivning',
    useDefaultTitle: true,
    // The two "(max 25 ord)" spellings are the pre-FC-2042 template headers.
    // They stay in the search list so files produced from an older template
    // still auto-map to this column.
    searchTitleList: [
      'Skriv en kort säljande beskrivning av produkten (max 155 tecken)',
      'Kort säljade beskrivning av produkten (max 155 tecken)',
      'Skriv en kort säljande beskrivning av produkten (max 25 ord)',
      'Kort säljade beskrivning av produkten (max 25 ord)',
      'Säljande beskrivning',
      'Skriv en kort säljande beskrivning av produkten',
    ],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Ingrediensförteckning',
    field: 'ingredient_statement',
    previewOrder: 14,
    previewGroup: { id: 'contents', label: 'Innehåll' },
    useDefaultTitle: true,
    searchTitleList: ['Innehåll. Ange ämnen/ingredienser som produkten innehåller'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mergeColumns: {
      isColumnToMerge: ({ excelTitle, foodlaCategory, rootCategory }) => {
        return (
          ['Innehåll. Ange ämnen/ingredienser separerade med kommatecken, samt mängd och % av DRI.'].includes(
            excelTitle
          ) && !isNewNonFood(foodlaCategory, rootCategory)
        );
      },
      mergeValue: ({ excelValue }) => {
        return { value: excelValue };
      },
    },
  },
  {
    title: 'Fodersammansättning',
    field: 'animalFoodIngredients',
    previewOrder: 18,
    previewGroup: { id: 'feed', label: 'Foderuppgifter' },
    fieldMappingName: 'Fodersammansättning',
    showInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
  },
  {
    title: 'Innehållsförteckning',
    field: 'non_food_ingredients',
    previewOrder: 25,
    previewGroup: { id: 'ingredients', label: 'Innehåll och märkningar' },
    useDefaultTitle: true,
    searchTitleList: ['Innehåll. Ange ämnen/ingredienser som produkten innehåller'],
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
      GROUP_CATEGORY_IDS.RECEPTFRIA_LAKEMEDEL,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
      GROUP_CATEGORY_IDS.RECEPTFRIA_LAKEMEDEL,
    ],
    mergeColumns: {
      isColumnToMerge: ({ excelTitle, foodlaCategory, rootCategory }) => {
        return (
          ['Innehåll. Ange ämnen/ingredienser separerade med kommatecken, samt mängd och % av DRI.'].includes(
            excelTitle
          ) && isNewNonFood(foodlaCategory, rootCategory)
        );
      },
      mergeValue: ({ excelValue }) => {
        return { value: excelValue };
      },
    },
  },
  {
    title: 'Märkningar',
    field: 'labels',
    previewOrder: 26,
    previewGroup: { id: 'ingredients', label: 'Innehåll och märkningar' },
    useDefaultTitle: true,
    searchTitleList: ['Eventuella Märkningar (t.ex. Svanen, Certified Vegan)', 'Eventuella Märkningar'],
    mergeColumns: {
      isColumnToMerge: params => {
        return (
          labelsCheckMergeColumnFn.vegan(params) ||
          labelsCheckMergeColumnFn.glutenFree(params) ||
          labelsCheckMergeColumnFn.apotea(params)
        );
      },
      mergeValue: params => {
        const { excelValue, oldValue } = params;
        const booleanValue = convertStringToBoolean(excelValue as string);
        const list = ((oldValue || '') as string).split(',').filter(val => !isBlank(val));
        const invalidValues: string[] = [];

        if (labelsCheckMergeColumnFn.vegan(params)) {
          if (booleanValue) list.push('Veganskt');
        } else if (labelsCheckMergeColumnFn.glutenFree(params)) {
          if (booleanValue) list.push('Glutenfri (text)');
        }
        if (!isBlank(excelValue) && labelsCheckMergeColumnFn.apotea(params)) {
          const apoteaLabelNames = Object.keys(APOTEA_LABEL_NAME);
          if (apoteaLabelNames.includes(excelValue as string)) {
            list.push(excelValue as string);
          } else {
            invalidValues.push(excelValue as string);
          }
        }

        return {
          value: list.join(','),
          warnings: invalidValues.map(val => `Ogiltigt värde: "${val}"`),
        };
      },
    },
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
    suggestions: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Acceptabla värden</div>
        {getProductMarkings().map(({ label, listData }, index) => (
          <div key={index}>
            {label && <p style={{ fontWeight: 700 }}>{label}</p>}
            {renderSuggestionList(listData, { notTitle: true })}
          </div>
        ))}
      </div>
    ),
  },
  {
    title: 'Näringsenhet (gram / milliliter / dagsdos)',
    field: 'nutritional_unit',
    previewOrder: 27,
    previewGroup: { id: 'nutrition', label: 'Näringsdeklaration' },
    previewTitle: 'Näringsvärde per',
    fieldMappingName: 'Näringsenhet: gram, milliliter eller dagsdos',
    defaultValue: 'gram',
    searchTitleList: [
      'Näringsenhet: Gram eller milliliter (näringsvärde per 100)',
      'Näringsvärde per 100 Gram eller milliliter',
      'Näringsenhet: gram, milliliter eller dagsdos',
    ],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Vitamin A',
    field: 'vitaminA',
    previewOrder: 43,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Vitamin A (μg)',
    fieldMappingName: 'Vitamin A',
    searchTitleList: ['Vitamin A μg', 'Vitamin A mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Vitamin D',
    field: 'vitaminD',
    previewOrder: 44,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Vitamin D (μg)',
    fieldMappingName: 'Vitamin D',
    searchTitleList: ['Vitamin D μg', 'Vitamin D mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Vitamin E',
    field: 'vitaminE',
    previewOrder: 45,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Vitamin E (mg)',
    fieldMappingName: 'Vitamin E',
    searchTitleList: ['Vitamin E μg', 'Vitamin E mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Vitamin K',
    field: 'vitaminK',
    previewOrder: 46,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Vitamin K (μg)',
    fieldMappingName: 'Vitamin K',
    searchTitleList: ['Vitamin K μg', 'Vitamin K mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Vitamin C',
    field: 'vitaminC',
    previewOrder: 47,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Vitamin C (mg)',
    fieldMappingName: 'Vitamin C',
    searchTitleList: ['Vitamin C μg', 'Vitamin C mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Tiamin',
    field: 'tiamin',
    previewOrder: 48,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Tiamin (mg)',
    fieldMappingName: 'Tiamin',
    searchTitleList: ['Tiamin μg', 'Tiamin mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Riboflavin',
    field: 'riboflavin',
    previewOrder: 49,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Riboflavin (mg)',
    fieldMappingName: 'Riboflavin',
    searchTitleList: ['Riboflavin μg', 'Riboflavin mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Niacin',
    field: 'niacin',
    previewOrder: 50,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Niacin (mg)',
    fieldMappingName: 'Niacin',
    searchTitleList: ['Niacin μg', 'Niacin mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Vitamin B6',
    field: 'vitaminB6',
    previewOrder: 51,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Vitamin B6 (mg)',
    fieldMappingName: 'Vitamin B6',
    searchTitleList: ['Vitamin B6 μg', 'Vitamin B6 mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Folsyra',
    field: 'folsyra',
    previewOrder: 52,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Folsyra (μg)',
    fieldMappingName: 'Folsyra',
    searchTitleList: ['Folsyra μg', 'Folsyra mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Vitamin B12',
    field: 'vitaminB12',
    previewOrder: 53,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Vitamin B12 (μg)',
    fieldMappingName: 'Vitamin B12',
    searchTitleList: ['Vitamin B12 μg', 'Vitamin B12 mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Biotin',
    field: 'biotin',
    previewOrder: 54,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Biotin (μg)',
    fieldMappingName: 'Biotin',
    searchTitleList: ['Biotin μg', 'Biotin mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Pantotensyra',
    field: 'pantotensyra',
    previewOrder: 55,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Pantotensyra (mg)',
    fieldMappingName: 'Pantotensyra',
    searchTitleList: ['Pantoensyra', 'Pantoensyra μg', 'Pantoensyra mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Kalium',
    field: 'kalium',
    previewOrder: 56,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Kalium (mg)',
    fieldMappingName: 'Kalium',
    searchTitleList: ['Kalium μg', 'Kalium mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Klorid',
    field: 'klorid',
    previewOrder: 57,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Klorid (mg)',
    fieldMappingName: 'Klorid',
    searchTitleList: ['Klorid μg', 'Klorid mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Kalcium',
    field: 'kalcium',
    previewOrder: 58,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Kalcium (mg)',
    fieldMappingName: 'Kalcium',
    searchTitleList: ['Kalcium μg', 'Kalcium mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Fosfor',
    field: 'fosfor',
    previewOrder: 59,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Fosfor (mg)',
    fieldMappingName: 'Fosfor',
    searchTitleList: ['Fosfor μg', 'Fosfor mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Magnesium',
    field: 'magnesium',
    previewOrder: 60,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Magnesium (mg)',
    fieldMappingName: 'Magnesium',
    searchTitleList: ['Magnesium μg', 'Magnesium mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Järn',
    field: 'jarn',
    previewOrder: 61,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Järn (mg)',
    fieldMappingName: 'Järn',
    searchTitleList: ['Järn μg', 'Järn mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Zink',
    field: 'zink',
    previewOrder: 62,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Zink (mg)',
    fieldMappingName: 'Zink',
    searchTitleList: ['Zink μg', 'Zink mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Koppar',
    field: 'koppar',
    previewOrder: 63,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Koppar (mg)',
    fieldMappingName: 'Koppar',
    searchTitleList: ['Koppar μg', 'Koppar mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Mangan',
    field: 'mangan',
    previewOrder: 64,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Mangan (mg)',
    fieldMappingName: 'Mangan',
    searchTitleList: ['Mangan μg', 'Mangan mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Fluorid',
    field: 'fluorid',
    previewOrder: 65,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Fluorid (mg)',
    fieldMappingName: 'Fluorid',
    searchTitleList: ['Fluorid μg', 'Fluorid mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Selen',
    field: 'selen',
    previewOrder: 66,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Selen (μg)',
    fieldMappingName: 'Selen',
    searchTitleList: ['Selen μg', 'Selen mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Krom',
    field: 'krom',
    previewOrder: 67,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Krom (μg)',
    fieldMappingName: 'Krom',
    searchTitleList: ['Krom μg', 'Krom mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Molybden',
    field: 'molybden',
    previewOrder: 68,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Molybden (μg)',
    fieldMappingName: 'Molybden',
    searchTitleList: ['Molybden μg', 'Molybden mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Jod',
    field: 'jod',
    previewOrder: 69,
    previewGroup: { id: 'vitamins', label: 'Vitaminer & Mineraler' },
    previewTitle: 'Jod (μg)',
    fieldMappingName: 'Jod',
    searchTitleList: ['Jod μg', 'Jod mg'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Energi kJ',
    field: 'energi_kj',
    previewOrder: 30,
    previewGroup: { id: 'nutrition', label: 'Näringsdeklaration' },
    previewTitle: 'Energi (kJ)',
    fieldMappingName: 'Energi kJ',
    searchTitleList: ['Energi'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Energi kcal',
    field: 'energi_kcal',
    previewOrder: 31,
    previewGroup: { id: 'nutrition', label: 'Näringsdeklaration' },
    previewTitle: 'Energi (kcal)',
    fieldMappingName: 'Energi kcal',
    searchTitleList: ['Kcal'],
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Fett',
    field: 'fett',
    unit: 'g',
    previewOrder: 32,
    previewGroup: { id: 'nutrition', label: 'Näringsdeklaration' },
    fieldMappingName: 'Fett',
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Mättat fett',
    field: 'mattatFett',
    unit: 'g',
    previewOrder: 33,
    previewGroup: { id: 'nutrition', label: 'Näringsdeklaration' },
    previewTitle: 'Varav mättat fett',
    fieldMappingName: 'Mättat fett',
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Enkelomättat fett',
    field: 'enkelomattatFett',
    unit: 'g',
    previewOrder: 34,
    previewGroup: { id: 'nutrition', label: 'Näringsdeklaration' },
    previewTitle: 'Varav enkelomättat fett',
    fieldMappingName: 'Enkelomättat fett',
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Fleromättat fett',
    field: 'flerromattatFett',
    unit: 'g',
    previewOrder: 35,
    previewGroup: { id: 'nutrition', label: 'Näringsdeklaration' },
    previewTitle: 'Varav fleromättat fett',
    fieldMappingName: 'Fleromättat fett',
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Kolhydrat',
    field: 'kolhydrat',
    unit: 'g',
    previewOrder: 36,
    previewGroup: { id: 'nutrition', label: 'Näringsdeklaration' },
    fieldMappingName: 'Kolhydrat',
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Sockerarter',
    field: 'sockerarter',
    unit: 'g',
    previewOrder: 37,
    previewGroup: { id: 'nutrition', label: 'Näringsdeklaration' },
    previewTitle: 'Varav sockerarter',
    fieldMappingName: 'Sockerarter',
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Polyoler',
    field: 'polyoler',
    unit: 'g',
    previewOrder: 38,
    previewGroup: { id: 'nutrition', label: 'Näringsdeklaration' },
    previewTitle: 'Varav polyoler',
    fieldMappingName: 'Polyoler',
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Stärkelse',
    field: 'starkelse',
    unit: 'g',
    previewOrder: 39,
    previewGroup: { id: 'nutrition', label: 'Näringsdeklaration' },
    previewTitle: 'Varav stärkelse',
    fieldMappingName: 'Stärkelse',
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Fiber',
    field: 'fiber',
    unit: 'g',
    previewOrder: 40,
    previewGroup: { id: 'nutrition', label: 'Näringsdeklaration' },
    fieldMappingName: 'Fiber',
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Protein',
    field: 'protein',
    unit: 'g',
    previewOrder: 41,
    previewGroup: { id: 'nutrition', label: 'Näringsdeklaration' },
    fieldMappingName: 'Protein',
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Salt',
    field: 'salt',
    unit: 'g',
    previewOrder: 42,
    previewGroup: { id: 'nutrition', label: 'Näringsdeklaration' },
    fieldMappingName: 'Salt',
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Nettokvantitet',
    field: 'descriptive_size_amount',
    previewOrder: 121,
    previewGroup: { id: 'quantity', label: 'Nettokvantitet' },
    previewTitle: 'Mängd',
    useDefaultTitle: true,
    searchTitleList: ['Nettokvantitet mängd', 'Mängd'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
  },
  {
    title: 'Nettokvantitet (ytterligare mängd)',
    field: 'descriptive_size_amount_extra',
    previewOrder: 123,
    previewGroup: { id: 'quantity', label: 'Nettokvantitet' },
    previewTitle: 'Ytterligare mängd (gram)',
    fieldMappingName: 'Nettokvantitet (ytterligare mängd gram)',
    searchTitleList: ['Nettokvantitet mängd extra', 'Nettokvantitet (ytterligare mängd gram)'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Enhet',
    field: 'descriptive_size_unit',
    previewOrder: 122,
    previewGroup: { id: 'quantity', label: 'Nettokvantitet' },
    useDefaultTitle: true,
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    searchTitleList: ['Enhet'],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
  },
  {
    title: 'Förvaringstemperatur min',
    field: 'min_temperature',
    previewOrder: 128,
    previewGroup: { id: 'storageTemperature', label: 'Förvaringstemperatur' },
    previewTitle: 'Min:',
    fieldMappingName: 'Förvaringstemperatur min',
    searchTitleList: ['Förvaringstemperatur Min (°C)'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Förvaringstemperatur max',
    field: 'max_temperature',
    previewOrder: 129,
    previewGroup: { id: 'storageTemperature', label: 'Förvaringstemperatur' },
    previewTitle: 'Max:',
    fieldMappingName: 'Förvaringstemperatur max',
    searchTitleList: ['Förvaringstemperatur Max (°C)'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Förvaringsanvisning',
    field: 'trade_item_temperature_information',
    previewOrder: 132,
    previewGroup: { id: 'storageInstructions', label: 'Förvaringsanvisning' },
    fieldMappingName: 'Förvaringsanvisning',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Ursprungsland och härkomstplats',
    field: 'place_of_item_activity',
    previewOrder: 144,
    previewGroup: { id: 'origin', label: 'Ursprung' },
    fieldMappingName: 'Ursprungsland och härkomstplats',
    searchTitleList: ['Ursprungsland och härkomstplats', 'Ursprungsland och härkomplats'],
    showInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
      GROUP_CATEGORY_IDS.RECEPTFRIA_LAKEMEDEL,
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Produktionsmetod',
    field: 'fishProductionMethod',
    previewOrder: 15,
    previewGroup: { id: 'fish', label: 'Fiskuppgifter' },
    fieldMappingName: 'Produktionsmetod',
    searchTitleList: ['Produktionsmetod\nInlandsfiske, Havsfiske, Odling'],
    showInCategories: [GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER],
    mandatoryInCategories: [],
    suggestions: renderSuggestionList(fishProductionMethod),
  },
  {
    title: 'Fiskemetod',
    field: 'fishCatchMethod',
    previewOrder: 16,
    previewGroup: { id: 'fish', label: 'Fiskuppgifter' },
    fieldMappingName: 'Fiskemetod',
    searchTitleList: ['Fiskemetod\ne.g. Trålar'],
    showInCategories: [GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER],
    mandatoryInCategories: [],
    suggestions: renderSuggestionList(fishCatchMethod),
  },
  {
    title: 'Fångstområde',
    field: 'catchArea',
    previewOrder: 17,
    previewGroup: { id: 'fish', label: 'Fiskuppgifter' },
    fieldMappingName: 'Fångstområde',
    searchTitleList: ['Fångstområde\ne.g. Norra Ishavet'],
    showInCategories: [GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER],
    mandatoryInCategories: [],
    suggestions: renderSuggestionList(catchArea),
  },
  {
    title: 'Ägg viktklass',
    field: 'eggWeightClass',
    previewOrder: 2,
    previewGroup: { id: 'category', label: 'Kategori' },
    previewTitle: 'Viktklass (ägg)',
    fieldMappingName: 'Ägg viktklass',
    searchTitleList: ['Klass A Viktklass\nS, S/M, M, M/L, L, L/XL, XL'],
    showInCategories: [GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Godkännandenummer för äggpackeri',
    field: 'eggPackerApprovalNumber',
    previewOrder: 10,
    previewGroup: { id: 'identity', label: 'Produktinformation' },
    fieldMappingName: 'Godkännandenummer för äggpackeri',
    searchTitleList: ['Ägg godkännandenummer äggpackeri'],
    showInCategories: [GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER],
    mandatoryInCategories: [GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER],
  },
  {
    title: 'Frukt och grönt klassifiering',
    field: 'classification',
    previewOrder: 3,
    previewGroup: { id: 'category', label: 'Kategori' },
    previewTitle: 'Klassificering',
    fieldMappingName: 'Frukt och grönt klassifiering',
    searchTitleList: ['Klassifiering\nKlass 1, 2, 3', 'Klassifiering: Klass 1, 2, 3'],
    showInCategories: [GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER],
    mandatoryInCategories: [GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER],
  },
  {
    title: 'Alkoholhalt',
    field: 'alcoholPercentage',
    previewOrder: 4,
    previewGroup: { id: 'category', label: 'Kategori' },
    fieldMappingName: 'Alkoholhalt',
    showInCategories: [GROUP_CATEGORY_IDS.DRYCKER],
    mandatoryInCategories: [],
  },
  {
    title: 'Panttyp',
    field: 'recyclingType',
    previewOrder: 5,
    previewGroup: { id: 'category', label: 'Kategori' },
    fieldMappingName: 'Panttyp',
    searchTitleList: ['Panttyp\ne.g. glasflaska 500 ml 12%'],
    showInCategories: [GROUP_CATEGORY_IDS.DRYCKER],
    mandatoryInCategories: [GROUP_CATEGORY_IDS.DRYCKER],
    suggestions: renderSuggestionList(recyclingType),
  },
  {
    title: 'Pantbelopp',
    field: 'recycling_amount',
    previewOrder: 6,
    previewGroup: { id: 'category', label: 'Kategori' },
    unit: 'SEK',
    showInCategories: [GROUP_CATEGORY_IDS.DRYCKER],
    mandatoryInCategories: [],
  },
  {
    title: 'Tillverkningsland',
    field: 'country_of_manufacturing_string',
    previewOrder: 145,
    previewGroup: { id: 'manufacturing', label: 'Tillverkningsland' },
    useDefaultTitle: true,
    searchTitleList: ['Ursprungsland', 'Tillverkningsland', 'Ange ursprungsland'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER],
    suggestions: renderSuggestionList(countries.map(({ sv }) => ({ displayName: sv }))),
  },
  {
    title: 'Sökbegrepp och nyckelord',
    field: 'search_terms',
    previewOrder: 155,
    previewGroup: { id: 'search', label: 'Sökbegrepp' },
    useDefaultTitle: true,
    searchTitleList: ['Ange sökord för produkten'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'B-packstorlek (Kfp/Dfp)',
    field: 'consumer_size_to_order_size_ratio',
    previewOrder: 168,
    previewGroup: { id: 'packQuantities', label: 'Logistik: förpackningsantal' },
    previewTitle: 'B-packstorlek, antal konsumentförpackningar per distributionsförpackning (kfp/dfp)',
    useDefaultTitle: true,
    searchTitleList: [
      'B-packstorlek (Antal konsumentförpackningar per distributionsförpackning, Kfp/Dfp)',
      'B-packstorlek (Kfp/Dfp)',
      'B-packstorlek, antal konsumentförpackningar per distributionsförpackning (Kfp/Dfp)',
      'B-packstorlek',
      'Bpackstorlek',
      'B packstorlek',
      'Kfp/Dfp',
      'Antal konsumentförpackningar per distributionsförpackning (Kfp/Dfp)',
      'Antal konsumentförpackningar per distributionsförpackning',
      'Antal konsumentförpackningar per detaljistförpackning (DFP) eller B-pack',
      'Antal konsument-förpackningar per detaljistförpackning (DFP) eller B-pack',
    ],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Bruttovikt',
    field: 'gross_weight_num',
    unit: 'gram',
    previewOrder: 124,
    previewGroup: { id: 'grossWeight', label: 'Bruttovikt' },
    fieldMappingName: 'Bruttovikt',
    searchTitleList: ['Bruttovikt', 'Bruttovikt konsumentförpackning (gram)'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Konsumentförpackningens dimensioner Höjd (MM)',
    field: 'gross_height',
    unit: 'millimeter',
    previewOrder: 125,
    previewGroup: { id: 'dimensions', label: 'Konsumentförpackningens dimensioner' },
    previewTitle: 'Höjd',
    fieldMappingName: 'Konsumentförpackningens höjd (MM)',
    searchTitleList: [
      'Höjd',
      'Konsumentförpackningens dimensioner Höjd',
      'Konsumentförpackningens höjd (MM)',
      'Förpackningens dimensioner Höjd (MM)',
    ],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Konsumentförpackningens dimensioner Bredd (MM)',
    field: 'gross_width',
    unit: 'millimeter',
    previewOrder: 126,
    previewGroup: { id: 'dimensions', label: 'Konsumentförpackningens dimensioner' },
    previewTitle: 'Bredd',
    fieldMappingName: 'Konsumentförpackningens bredd (MM)',
    searchTitleList: [
      'Bredd',
      'Konsumentförpackningens dimensioner Bredd',
      'Konsumentförpackningens bredd (MM)',
      'Förpackningens dimensioner Bredd (MM)',
    ],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Konsumentförpackningens dimensioner Djup (MM)',
    field: 'gross_depth',
    unit: 'millimeter',
    previewOrder: 127,
    previewGroup: { id: 'dimensions', label: 'Konsumentförpackningens dimensioner' },
    previewTitle: 'Djup',
    fieldMappingName: 'Konsumentförpackningens djup (MM)',
    searchTitleList: [
      'Djup',
      'Konsumentförpackningens dimensioner Djup',
      'Konsumentförpackningens djup (MM)',
      'Förpackningens dimensioner Djup (MM)',
    ],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Varumärke',
    field: 'brand',
    previewOrder: 148,
    previewGroup: { id: 'brand', label: 'Varumärke' },
    fieldMappingName: 'Varumärke',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Varumärke',
    field: 'brand_food',
    previewOrder: 147,
    previewGroup: { id: 'brand', label: 'Varumärke' },
    fieldMappingName: 'Varumärke',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Adress till producent',
    field: 'location',
    previewOrder: 178,
    previewGroup: { id: 'location', label: 'Producentadress' },
    fieldMappingName: 'Adress till producent',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Färg',
    field: 'color',
    previewOrder: 71,
    previewGroup: { id: 'properties', label: 'Produktegenskaper' },
    fieldMappingName: 'Färg',
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Material',
    field: 'material',
    previewOrder: 72,
    previewGroup: { id: 'properties', label: 'Produktegenskaper' },
    fieldMappingName: 'Material',
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Mått',
    field: 'dimensions',
    previewOrder: 73,
    previewGroup: { id: 'properties', label: 'Produktegenskaper' },
    fieldMappingName: 'Mått',
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Volym',
    field: 'capacity',
    previewOrder: 74,
    previewGroup: { id: 'properties', label: 'Produktegenskaper' },
    fieldMappingName: 'Volym',
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Användningsinstruktioner och dosering',
    field: 'dosageAndUsageInstructions',
    previewOrder: 135,
    previewGroup: { id: 'dosage', label: 'Användningsinstruktioner och dosering' },
    useDefaultTitle: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
      GROUP_CATEGORY_IDS.RECEPTFRIA_LAKEMEDEL,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
      GROUP_CATEGORY_IDS.RECEPTFRIA_LAKEMEDEL,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
    ],
    mergeColumns: {
      isColumnToMerge: excelColumn => {
        return (
          dosageAndUsageInstructionsCheckMergeColumnFn.dosering(excelColumn) ||
          dosageAndUsageInstructionsCheckMergeColumnFn.warning(excelColumn)
        );
      },
      mergeValue: params => {
        const { excelValue, oldValue } = params;
        if (isBlank(excelValue)) return { value: oldValue };
        let result: string[] = [];

        if (dosageAndUsageInstructionsCheckMergeColumnFn.dosering(params)) {
          result = [excelValue as string, oldValue as string];
        }
        if (dosageAndUsageInstructionsCheckMergeColumnFn.warning(params)) {
          result = [oldValue as string, excelValue as string];
        }

        return { value: result.filter(val => !isBlank(val)).join('\n\n') };
      },
    },
  },
  {
    title: 'Tillagningsanvisning',
    field: 'preparationInstructions',
    previewOrder: 133,
    previewGroup: { id: 'preparation', label: 'Tillagningsanvisning' },
    fieldMappingName: 'Tillagningsanvisning',
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Signalord',
    field: 'signalWords',
    previewOrder: 136,
    previewGroup: { id: 'hazards', label: 'Faroinformation' },
    fieldMappingName: 'Signalord',
    searchTitleList: ['Signalord\nFara, Varning, eller Ej Tillämpbar'],
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
    ],
    mandatoryInCategories: [GROUP_CATEGORY_IDS.LEKSAK, GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER],
  },
  {
    title: 'Ange ett eller flera faropiktogram',
    field: 'hazardSymbols',
    previewOrder: 137,
    previewGroup: { id: 'hazards', label: 'Faroinformation' },
    previewTitle: 'Välj ett eller flera faropiktogram',
    fieldMappingName: 'Ange ett eller flera faropiktogram',
    searchTitleList: ['Ange ett eller flera faropiktogram\ne.g. explosivt, giftigt'],
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Faroangivelser Kod',
    field: 'hazardStatements',
    previewOrder: 139,
    previewGroup: { id: 'hazards', label: 'Faroinformation' },
    fieldMappingName: 'Faroangivelser kod',
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Skyddsangivelser Kod',
    field: 'securityNotices',
    previewOrder: 141,
    previewGroup: { id: 'hazards', label: 'Faroinformation' },
    previewTitle: 'Skyddsangivelser',
    fieldMappingName: 'Skyddsangivelser kod',
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Tillägg till faroangivelser',
    field: 'hazardStatementFills',
    previewOrder: 140,
    previewGroup: { id: 'hazards', label: 'Faroinformation' },
    previewTitle: 'Produktspecifika tillägg till valda faroangivelser',
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Tillägg till skyddsangivelser',
    field: 'securityNoticeFills',
    previewOrder: 142,
    previewGroup: { id: 'hazards', label: 'Faroinformation' },
    previewTitle: 'Produktspecifika tillägg till valda skyddsangivelser',
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Godkännande nummer ansvarig märkning',
    field: 'responsibleLabelingApprovalNumber',
    previewOrder: 22,
    previewGroup: { id: 'feed', label: 'Foderuppgifter' },
    previewTitle: 'Godkännandenummer ansvarig för märkning eller ansvarig för märkning',
    fieldMappingName: 'Godkännande nummer ansvarig märkning',
    searchTitleList: [
      'Godkännande nummer ansvarig märkning',
      'Godkännandenummer ansvarig för märkning eller ansvarig för märkning',
    ],
    showInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
  },
  {
    title: 'Djurkategori för foder',
    field: 'animalCategoriesForFeed',
    previewOrder: 23,
    previewGroup: { id: 'feed', label: 'Foderuppgifter' },
    fieldMappingName: 'Djurkategori för foder',
    searchTitleList: ['Djurkategori för foder\ne.g Häst, hund, katt'],
    showInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
    suggestions: renderSuggestionList(animalCategoriesForFeed),
  },
  {
    title: 'Fodertillsatser',
    field: 'feedAdditives',
    previewOrder: 19,
    previewGroup: { id: 'feed', label: 'Foderuppgifter' },
    fieldMappingName: 'Fodertillsatser',
    showInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
  },
  {
    title: 'Analytiska beståndsdelar',
    field: 'analyticalConstituentsFeed',
    previewOrder: 21,
    previewGroup: { id: 'feed', label: 'Foderuppgifter' },
    fieldMappingName: 'Analytiska beståndsdelar',
    showInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
  },
  {
    title: 'Utfordringsinstruktioner',
    field: 'feedingInstructions',
    previewOrder: 20,
    previewGroup: { id: 'feed', label: 'Foderuppgifter' },
    previewTitle: 'Utfodringsinstruktioner',
    fieldMappingName: 'Utfordringsinstruktioner',
    searchTitleList: ['Utfodringsinstruktioner'],
    showInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
  },
  {
    title: 'Ålderskategori',
    field: 'animalAgeGroup',
    previewOrder: 24,
    previewGroup: { id: 'feed', label: 'Foderuppgifter' },
    fieldMappingName: 'Ålderskategori',
    searchTitleList: ['Ålderskategori\ne.g. 5-8 år'],
    showInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Beredningsform',
    field: 'preparedForm',
    previewOrder: 134,
    previewGroup: { id: 'preparedForm', label: 'Beredningsform' },
    fieldMappingName: 'Beredningsform',
    searchTitleList: ['Beredningsform\ne.g tabeletter, spray, våtservett'],
    showInCategories: [
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.RECEPTFRIA_LAKEMEDEL,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
    ],
    mandatoryInCategories: [GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER, GROUP_CATEGORY_IDS.RECEPTFRIA_LAKEMEDEL],
    suggestions: renderSuggestionList(preparedForm),
  },
  {
    title: 'Solskyddsfaktor (SPF)',
    field: 'sunProtectionFactor',
    previewOrder: 190,
    previewGroup: { id: 'cosmeticCompliance', label: 'Kategori: kosmetika och solskydd' },
    fieldMappingName: 'Solskyddsfaktor (SPF)',
    searchTitleList: ['Solskyddsfaktor (SPF)\ne.g. 30, 50, 50+'],
    showInCategories: [GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER, GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL],
    mandatoryInCategories: [GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL],
  },
  {
    title:
      'I det fall produkten är avsedd för viktminskning, ange kategori (måltidsersättning/LCD/VLCD) samt i tillämpliga fall bifoga tillstånd från Livsmedelsverket för VLCD',
    field: 'categoryWeightloss',
    previewOrder: 180,
    previewGroup: { id: 'weightControl', label: 'Kategori: viktkontroll' },
    previewTitle: 'Ange kategori om produkten är för viktminskning',
    fieldMappingName: 'Viktminskning kategori (måltidsersättning, LCD, VLCD)',
    searchTitleList: [
      'Viktminskning kategori',
      'Viktminskning kategori (måltidsersättning, LCD, VLCD)',
      'För viktminskning produkter, ange kategori (måltidsersättning/LCD/VLCD) samt i tillämpliga fall ladda upp tillstånd från Livsmedelsverket för VLCD i din Sygrid profil',
    ],
    showInCategories: [GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39],
    mandatoryInCategories: [],
    suggestions: renderSuggestionList(categoryWeightloss),
  },
  {
    title: 'Är produkten CE-märkt?',
    field: 'ceMarking',
    previewOrder: 200,
    previewGroup: { id: 'ce', label: 'Produktfrågor: CE-märkning' },
    fieldMappingName: 'CE-märkt',
    searchTitleList: ['CE-märkt', 'CE-märkning'],
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
    ],
    mandatoryInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    defaultValue: 'false',
  },
  {
    title: 'Ange CE standarder/krav eller "inte CE märkt"',
    field: 'specifyCEStandards',
    previewOrder: 201,
    previewGroup: { id: 'ce', label: 'Produktfrågor: CE-märkning' },
    previewTitle: 'Ange mot vilka standarder/krav',
    fieldMappingName: 'CE standarder/krav',
    searchTitleList: ['CE standarder/krav'],
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
    ],
    mandatoryInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
  },
  {
    title: 'Min Transporttemperatur',
    field: 'transportMinTemperature',
    previewOrder: 130,
    previewGroup: { id: 'transportTemperature', label: 'Transporttemperatur' },
    previewTitle: 'Min:',
    fieldMappingName: 'Transporttemperatur min',
    searchTitleList: ['Transport-temperatur min', 'Transporttemperatur min'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Max Transporttemperatur',
    field: 'transportMaxTemperature',
    previewOrder: 131,
    previewGroup: { id: 'transportTemperature', label: 'Transporttemperatur' },
    previewTitle: 'Max:',
    fieldMappingName: 'Transporttemperatur max',
    searchTitleList: ['Transport-temperatur max', 'Transporttemperatur max'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Antal distributionsförpackningar per transportförpackning (dfp/tfp)',
    field: 'transportQtyOfNextLowerItem',
    previewOrder: 170,
    previewGroup: { id: 'packQuantities', label: 'Logistik: förpackningsantal' },
    fieldMappingName: 'Antal distributionsförpackningar per transportförpackning (dfp/tfp)',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Antal transportförpackningar per pall (tfp/pall)',
    field: 'palletQtyOfNextLowerItem',
    previewOrder: 171,
    previewGroup: { id: 'packQuantities', label: 'Logistik: förpackningsantal' },
    fieldMappingName: 'Antal transportförpackningar per pall (tfp/pall)',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Antal distributionsförpackningar per pall (dfp/pall)',
    field: 'palletQtyOfStorePack',
    previewOrder: 169,
    previewGroup: { id: 'packQuantities', label: 'Logistik: förpackningsantal' },
    fieldMappingName: 'Antal distributionsförpackningar per pall (dfp/pall)',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Antal konsumentförpackningar per pall (kfp/pall)',
    useDefaultTitle: true,
    field: 'palletQtyOfBaseItem',
    previewOrder: 174,
    previewGroup: { id: 'packQuantities', label: 'Logistik: förpackningsantal' },
    searchTitleList: [
      'Antal konsumentförpackningar per pall',
      'Antal konsument-förpackningar per pall',
      'Antal konsument-förpackningar per pall.',
    ],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
    rowRuntimeFlags: {
      _skipPalletQtyDerivation: true,
    },
  },
  {
    title: 'Antal konsumentförpackningar per transportförpackning (kfp/tfp)',
    useDefaultTitle: true,
    field: 'transportQtyOfBaseItem',
    previewOrder: 172,
    previewGroup: { id: 'packQuantities', label: 'Logistik: förpackningsantal' },
    searchTitleList: [
      'Antal konsumentförpackningar per transportförpackning (TFP)',
      'Antal konsument-förpackningar per transportförpackning (TFP)',
      'Antal konsument-förpackningar per transportförpackning (TFP).',
    ],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
    rowRuntimeFlags: {
      _skipTransportQtyDerivation: true,
    },
  },
  {
    title: 'Pall beställningsbara?',
    field: 'palletOrderableUnit',
    previewOrder: 163,
    previewGroup: { id: 'orderable', label: 'Logistik: beställningsbara nivåer' },
    previewTitle: 'Pall',
    fieldMappingName: 'Pall beställningsbara?',
    searchTitleList: ['Pallet Pack- Is Orderable Unit?'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Används pall för transport?',
    field: 'palletDespatchUnit',
    previewOrder: 167,
    previewGroup: { id: 'despatch', label: 'Logistik: transportnivåer' },
    previewTitle: 'Pall',
    fieldMappingName: 'Används pall för transport?',
    searchTitleList: ['Pallet Pack- Is Despatch Unit?'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },

  // **** Logistic fields ****
  // EAN codes for packs other than consumer:
  {
    title: 'EAN-kod distributionsförpackning (14 siffror)',
    field: 'storePackEANForStorePack',
    fieldMappingName: 'EAN-kod distributionsförpackning (14 siffror)',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'EAN-kod transportförpackning (14 siffror)',
    field: 'transportEANForStorePack',
    fieldMappingName: 'EAN-kod transportförpackning (14 siffror)',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'EAN-kod pall (14 siffror)',
    field: 'palletEANForStorePack',
    fieldMappingName: 'EAN-kod pall (14 siffror)',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  // Orderable/despatch booleans:
  {
    title: 'Konsumentförpackning beställningsbara?',
    field: 'consumerPackOrderableUnit',
    previewOrder: 160,
    previewGroup: { id: 'orderable', label: 'Logistik: beställningsbara nivåer' },
    previewTitle: 'Konsumentförpackning',
    fieldMappingName: 'Konsumentförpackning beställningsbara?',
    searchTitleList: ['Consumer pack- Is Orderable Unit?'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Används konsumentförpackning för transport?',
    field: 'consumerPackDespatchUnit',
    previewOrder: 164,
    previewGroup: { id: 'despatch', label: 'Logistik: transportnivåer' },
    previewTitle: 'Konsumentförpackning',
    fieldMappingName: 'Används konsumentförpackning för transport?',
    searchTitleList: ['Consumer pack- Is Despatch Unit?', 'Konsumentförpackning för transport?'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Distributionsförpackning beställningsbara?',
    field: 'storePackOrderableUnit',
    previewOrder: 161,
    previewGroup: { id: 'orderable', label: 'Logistik: beställningsbara nivåer' },
    previewTitle: 'Distributionsförpackning',
    fieldMappingName: 'Distributionsförpackning beställningsbara?',
    searchTitleList: ['Store pack- Is Orderable Unit?'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Används distributionsförpackning för transport?',
    field: 'storePackDespatchUnit',
    previewOrder: 165,
    previewGroup: { id: 'despatch', label: 'Logistik: transportnivåer' },
    previewTitle: 'Distributionsförpackning',
    fieldMappingName: 'Används distributionsförpackning för transport?',
    searchTitleList: ['Distributionsförpackning för transport?', 'Store pack- Is Despatch Unit?'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Transportförpackning beställningsbara?',
    field: 'transportOrderableUnit',
    previewOrder: 162,
    previewGroup: { id: 'orderable', label: 'Logistik: beställningsbara nivåer' },
    previewTitle: 'Transportförpackning',
    fieldMappingName: 'Transportförpackning beställningsbara?',
    searchTitleList: ['Transport Pack- Is Orderable Unit?'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Transport Pack- Is Despatch Unit?',
    field: 'transportDespatchUnit',
    previewOrder: 166,
    previewGroup: { id: 'despatch', label: 'Logistik: transportnivåer' },
    previewTitle: 'Transportförpackning',
    fieldMappingName: 'Används transportförpackning för transport?',
    searchTitleList: [
      'Transportförpackning för transport? Transportförpackning för transport',
      'Används transportförpackning för transport?',
      'Transportförpackning för transport?',
      'Transport unit för transport',
      'Transport unit för dispatch',
      'Transport unit för despatch',
    ],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },

  // **** Date fields ****
  {
    title: 'Första leveransdag för nyheter',
    field: 'articleFirstDelivery',
    fieldMappingName: 'Första leveransdag för nyheter',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Första beställningsdag för nyheter',
    useDefaultTitle: true,
    field: 'articleFirstOrderDate',
    previewOrder: 176,
    previewGroup: { id: 'dates', label: 'Logistik: datum' },
    searchTitleList: ['Första beställningsdag'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Lanseringsdag mot konsument för nyheter',
    field: 'basicDataAvailabilityDateFrom',
    previewOrder: 175,
    previewGroup: { id: 'dates', label: 'Logistik: datum' },
    fieldMappingName: 'Lanseringsdag mot konsument för nyheter',
    searchTitleList: ['Lanseringsdag mot kosument för nyheter'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  // **** Tax fields ****
  {
    title: 'Produktens skattesats (6, 12, 25)',
    useDefaultTitle: true,
    field: 'basicDataTaxClassification',
    previewOrder: 179,
    previewGroup: { id: 'tax', label: 'Skattesats' },
    previewTitle: 'Produktens skattesats',
    searchTitleList: ['Produktens skattesats', 'Moms %'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },

  // **** Boolean question fields (and answer fields) ****
  {
    title: 'Innehåller produkten ett ämne som är förbjudet eller begränsat i REACH-förordningen?',
    field: 'reachSubstance',
    previewOrder: 212,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    fieldMappingName: 'Innehåller produkten ett ämne som är förbjudet eller begränsat i REACH-förordningen?',
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
    ],
  },
  {
    title: 'Har produkten ett bäst före datum?',
    useDefaultTitle: true,
    field: 'bestBeforeDate',
    previewOrder: 204,
    previewGroup: { id: 'packaging', label: 'Produktfrågor: förpackning och användning' },
    isExtra: true,
    defaultValue: 'false',
    mandatoryDefaultValue: 'true',
    searchTitleList: ['Har produkten ett bäst före datum som ska anges?'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
    ],
  },
  {
    title: 'Är produkten avsedd för barn under 16 år?',
    field: 'childrenUnder16',
    previewOrder: 205,
    previewGroup: { id: 'packaging', label: 'Produktfrågor: förpackning och användning' },
    fieldMappingName: 'Är produkten avsedd för barn under 16 år?',
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
      GROUP_CATEGORY_IDS.RECEPTFRIA_LAKEMEDEL,
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title:
      'Innehåller produkten något tillståndspliktigt ämne upptaget på bilaga XIV till Reach-förordningen EG 1907/2006?',
    field: 'reachSubstancesAnnex19072006',
    previewOrder: 192,
    previewGroup: { id: 'reach', label: 'Kategori: REACH' },
    previewTitle: 'Innehåller produkten något ämne med tillståndsplikt som anges i bilaga XIV till REACH-förordningen (EC) 1907/2006?',
    fieldMappingName:
      'Innehåller produkten något ämne med tillståndsplikt som anges i bilaga XIV till REACH-förordningen (EC) 1907/2006? *',
    isExtra: true,
    extraCorrespondingField: 'regulatedSubstance',
    searchTitleList: [
      'Innehåller produkten något ämne med tillståndsplikt som anges i bilaga XIV till REACH-förordningen (EC) 1907/2006? *',
    ],
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
    ],
  },
  {
    title:
      'Ange eventuellet något tillståndspliktigt ämne upptaget på bilaga XIV till Reach-förordningen EG 1907/2006.',
    field: 'regulatedSubstance',
    previewOrder: 193,
    previewGroup: { id: 'reach', label: 'Kategori: REACH' },
    previewTitle: 'Ange ämne(n)',
    fieldMappingName: 'Ange eventuellet tillståndspliktigt ämne (enligt bilaga XIV till REACH)',
    searchTitleList: [
      'Ange eventuellet tillståndspliktigt ämne (enligt bilaga XIV till REACH)',
      'Ange eventuellet tillståndspliktigt ämne',
    ],
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
    ],
  },
  {
    title:
      'Innehåller produkten något ämne på EU:s kandidatlista i en mängd som överstiger 0,1 viktprocent av produktens innehåll?',
    field: 'hazardousSubstancesOver1',
    previewOrder: 194,
    previewGroup: { id: 'hazardousSubstances', label: 'Kategori: ämnen och produktsäkerhet' },
    fieldMappingName:
      'Innehåller produkten något ämne på EU:s kandidatlista i en mängd som överstiger 0,1 viktprocent av produktens innehåll?',
    isExtra: true,
    extraCorrespondingField: 'informationHazardousSubstances',
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
    ],
  },
  {
    title:
      'Eventuellt ämne på EUs kandidat lista som överstiger 0,1% of produkts innehåll. Lista ämnen och information för säker användning',
    field: 'informationHazardousSubstances',
    previewOrder: 195,
    previewGroup: { id: 'hazardousSubstances', label: 'Kategori: ämnen och produktsäkerhet' },
    previewTitle: 'Lista särskilt farliga ämnen och information för säker användning',
    fieldMappingName: 'Eventuellt EU-kandidatlistat ämne och användnings information',
    searchTitleList: ['Eventuellt EU-kandidatlistat ämne och användnings information'],
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
    ],
  },
  {
    title: 'Har produkten en unik produktidentifiering (UDI)?',
    field: 'checkUniqueIdentification',
    previewOrder: 198,
    previewGroup: { id: 'medical', label: 'Kategori: medicinteknik' },
    fieldMappingName: 'Har produkten en unik produktidentifiering (UDI)?',
    isExtra: true,
    extraCorrespondingField: 'explanationNoUDI',
    defaultValue: 'true',
    showInCategories: [GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER],
    mandatoryInCategories: [GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER],
  },
  {
    title: 'Kommentar till Nej-svar',
    field: 'explanationNoUDI',
    previewOrder: 199,
    previewGroup: { id: 'medical', label: 'Kategori: medicinteknik' },
    previewTitle: 'Kommentar till eventuella nej-svar',
    fieldMappingName: 'Om produkten inte har UDI, kommentar till nej-svar',
    searchTitleList: ['Om produkten inte har UDI, kommentar till nej-svar'],
    showInCategories: [GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER],
    mandatoryInCategories: [GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER],
  },
  {
    title: 'Innehåller produkten CMR-klassade ämnen?',
    field: 'cmrSubstance',
    previewOrder: 210,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    fieldMappingName: 'Innehåller produkten CMR-klassade ämnen?',
    isExtra: true,
    extraCorrespondingField: 'CMRSubstances',
    searchTitleList: ['Eventuellt CMR ämne'],
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
      GROUP_CATEGORY_IDS.RECEPTFRIA_LAKEMEDEL,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
      GROUP_CATEGORY_IDS.RECEPTFRIA_LAKEMEDEL,
    ],
  },
  {
    title: 'Eventuellt CMR-klassat ämne',
    field: 'CMRSubstances',
    previewOrder: 211,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    previewTitle: 'CMR-klassat ämne',
    fieldMappingName: 'Eventuellt CMR-klassat ämne',
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
      GROUP_CATEGORY_IDS.RECEPTFRIA_LAKEMEDEL,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
      GROUP_CATEGORY_IDS.RECEPTFRIA_LAKEMEDEL,
    ],
  },
  {
    title: 'Är produkten godkänt som dietfoder?',
    field: 'approvalDietFeed',
    previewOrder: 182,
    previewGroup: { id: 'feedCompliance', label: 'Kategori: foder' },
    fieldMappingName: 'Är produkten godkänt som dietfoder?',
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
    mandatoryInCategories: [],
  },
  // Vitt snus
  {
    title: 'Mängden nikotin per portion angivet i milligram',
    useDefaultTitle: true,
    field: 'whiteSnusNicotinePerPortion',
    unit: 'mg',
    previewOrder: 90,
    previewGroup: { id: 'snus', label: 'Vitt snus' },
    previewTitle: 'Nikotin per portion',
    fieldMappingName: 'Nikotin per portion',
    searchTitleList: ['Nikotin per portion (Mängden nikotin per portion angivet i milligram. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'Mängden nikotin per gram angivet i milligram',
    useDefaultTitle: true,
    field: 'whiteSnusNicotinePerGram',
    unit: 'mg/g',
    previewOrder: 91,
    previewGroup: { id: 'snus', label: 'Vitt snus' },
    previewTitle: 'Nikotin per gram',
    fieldMappingName: 'Nikotin per gram',
    searchTitleList: ['Nikotin per gram (Mängden nikotin per gram angivet i milligram. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: `Beskrivning av nikotinstyrkan, t.ex. 'Stark', 'Medium', 'Svag'`,
    useDefaultTitle: true,
    field: 'whiteSnusNicotineStrength',
    previewOrder: 92,
    previewGroup: { id: 'snus', label: 'Vitt snus' },
    previewTitle: 'Nikotinstyrka',
    fieldMappingName: `Nikotinstyrka`,
    searchTitleList: [`Nikotinstyrka (Beskrivning av nikotinstyrkan, t.ex. 'Stark', 'Medium', 'Svag'. )`],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'Produktens smak eller smakprofil',
    useDefaultTitle: true,
    field: 'whiteSnusFlavor',
    previewOrder: 93,
    previewGroup: { id: 'snus', label: 'Vitt snus' },
    previewTitle: 'Smak',
    fieldMappingName: 'Smak',
    searchTitleList: ['Smak (Produktens smak eller smakprofil. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'Antal portioner per dosa/förpackning',
    useDefaultTitle: true,
    field: 'whiteSnusPortionsPerUnit',
    previewOrder: 94,
    previewGroup: { id: 'snus', label: 'Vitt snus' },
    previewTitle: 'Antal portioner/dosa',
    fieldMappingName: 'Antal portioner/dosa',
    searchTitleList: ['Antal portioner/dosa  (Antal portioner per dosa/förpackning. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'gram',
    useDefaultTitle: true,
    field: 'whiteSnusWeightPerPortion',
    unit: 'g',
    previewOrder: 95,
    previewGroup: { id: 'snus', label: 'Vitt snus' },
    previewTitle: 'Vikt per portion',
    fieldMappingName: 'Vikt per portion',
    searchTitleList: ['Vikt per portion (gram)'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'Produktens format, t.ex. Slim, Mini, Large, Normal',
    useDefaultTitle: true,
    field: 'whiteSnusFormat',
    previewOrder: 96,
    previewGroup: { id: 'snus', label: 'Vitt snus' },
    previewTitle: 'Format',
    fieldMappingName: 'Format',
    searchTitleList: ['Format (Produktens format, t.ex. Slim, Mini, Large, Normal. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'Vikt i gram, t.ex. 50',
    useDefaultTitle: true,
    field: 'yarnWeight',
    previewOrder: 76,
    previewGroup: { id: 'yarn', label: 'Garnspecifikation' },
    previewTitle: 'Vikt (gram)',
    fieldMappingName: 'Vikt',
    searchTitleList: ['Vikt garn', 'Vikt garn (Vikt i gram, t.ex. 50. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'Löplängd i meter, t.ex. 120',
    useDefaultTitle: true,
    field: 'yarnRunLength',
    previewOrder: 77,
    previewGroup: { id: 'yarn', label: 'Garnspecifikation' },
    previewTitle: 'Löplängd (meter)',
    fieldMappingName: 'Löplängd',
    searchTitleList: ['Löplängd', 'Löplängd (Löplängd i meter, t.ex. 120. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'Storlek i mm, t.ex. 2,5 eller 4-6',
    useDefaultTitle: true,
    field: 'recommendedCrochetHook',
    unit: 'mm',
    previewOrder: 78,
    previewGroup: { id: 'yarn', label: 'Garnspecifikation' },
    previewTitle: 'Rekommenderad virknål',
    fieldMappingName: 'Rekommenderad virknål',
    searchTitleList: ['Rekommenderad virknål', 'Rekommenderad virknål (Storlek i mm, t.ex. 2,5 eller 4-6. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'Storlek i mm, t.ex. 9 eller 2,5-3',
    useDefaultTitle: true,
    field: 'recommendedKnittingNeedles',
    unit: 'mm',
    previewOrder: 79,
    previewGroup: { id: 'yarn', label: 'Garnspecifikation' },
    previewTitle: 'Rekommenderade stickor',
    fieldMappingName: 'Rekommenderade stickor',
    searchTitleList: ['Rekommenderad virknål', 'Rekommenderade stickor (Storlek i mm, t.ex. 9 eller 2,5-3. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'Antal maskor, t.ex. 22',
    useDefaultTitle: true,
    field: 'knittingGaugeStitches',
    previewOrder: 80,
    previewGroup: { id: 'yarn', label: 'Garnspecifikation' },
    previewTitle: 'Stickfasthet — Maskor',
    fieldMappingName: 'Stickfasthet maskor',
    searchTitleList: ['Stickfasthet maskor', 'Stickfasthet maskor (Antal maskor, t.ex. 22. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'Antal varv, t.ex. 30',
    useDefaultTitle: true,
    field: 'knittingGaugeRows',
    previewOrder: 81,
    previewGroup: { id: 'yarn', label: 'Garnspecifikation' },
    previewTitle: 'Stickfasthet — Varv',
    fieldMappingName: 'Stickfasthet varv',
    searchTitleList: ['Stickfasthet varv', 'Stickfasthet varv (Antal varv, t.ex. 30. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'Antal maskor, t.ex. 14',
    useDefaultTitle: true,
    field: 'crochetGaugeStitches',
    previewOrder: 82,
    previewGroup: { id: 'yarn', label: 'Garnspecifikation' },
    previewTitle: 'Virkfasthet — Maskor',
    fieldMappingName: 'Virkfasthet maskor',
    searchTitleList: ['Virkfasthet maskor', 'Virkfasthet maskor (Antal maskor, t.ex. 14. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'Antal varv, t.ex. 12',
    useDefaultTitle: true,
    field: 'crochetGaugeRows',
    previewOrder: 83,
    previewGroup: { id: 'yarn', label: 'Garnspecifikation' },
    previewTitle: 'Virkfasthet — Varv',
    fieldMappingName: 'Virkfasthet varv',
    searchTitleList: ['Virkfasthet varv', 'Virkfasthet varv (Antal varv, t.ex. 12. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'Temperatur i °C, t.ex. 40',
    useDefaultTitle: true,
    field: 'washingTemperature',
    unit: '°C',
    previewOrder: 84,
    previewGroup: { id: 'textile', label: 'Skötselanvisningar (textil)' },
    previewTitle: 'Tvättemperatur',
    fieldMappingName: 'Tvättemperatur',
    searchTitleList: ['Tvättemperatur', 'Tvättemperatur (Temperatur i °C, t.ex. 40. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 't.ex. Skontvätt, ej centrifugering',
    useDefaultTitle: true,
    field: 'washingInstructions',
    previewOrder: 85,
    previewGroup: { id: 'textile', label: 'Skötselanvisningar (textil)' },
    previewTitle: 'Tvättråd',
    fieldMappingName: 'Tvättråd',
    searchTitleList: ['Tvättråd', 'Tvättråd (t.ex. Skontvätt, ej centrifugering. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'IRON_LOW, IRON_MEDIUM, IRON_HIGH eller DO_NOT_IRON',
    useDefaultTitle: true,
    field: 'ironing',
    previewOrder: 86,
    previewGroup: { id: 'textile', label: 'Skötselanvisningar (textil)' },
    previewTitle: 'Strykning',
    fieldMappingName: 'Strykning',
    searchTitleList: ['Strykning', 'Strykning (IRON_LOW, IRON_MEDIUM, IRON_HIGH eller DO_NOT_IRON. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'Ja eller Nej',
    useDefaultTitle: true,
    field: 'tumbleDrying',
    previewOrder: 87,
    previewGroup: { id: 'textile', label: 'Skötselanvisningar (textil)' },
    previewTitle: 'Torktumling',
    fieldMappingName: 'Torktumling',
    searchTitleList: ['Torktumling', 'Torktumling (Ja eller Nej. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'Ja eller Nej',
    useDefaultTitle: true,
    field: 'dryCleaning',
    previewOrder: 88,
    previewGroup: { id: 'textile', label: 'Skötselanvisningar (textil)' },
    previewTitle: 'Kemtvätt',
    fieldMappingName: 'Kemtvätt',
    searchTitleList: ['Kemtvätt', 'Kemtvätt (Ja eller Nej. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'Ja eller Nej',
    useDefaultTitle: true,
    field: 'chlorineBleaching',
    previewOrder: 89,
    previewGroup: { id: 'textile', label: 'Skötselanvisningar (textil)' },
    previewTitle: 'Klorblekning',
    fieldMappingName: 'Klorblekning',
    searchTitleList: ['Klorblekning', 'Klorblekning (Ja eller Nej. )'],
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },

  {
    title: 'Egenskaper',
    useDefaultTitle: true,
    field: 'additionalCharacteristics',
    previewOrder: 146,
    previewGroup: { id: 'characteristics', label: 'Ytterligare egenskaper' },
    previewTitle: 'Ytterligare egenskaper',
    searchTitleList: ['Egenskaper'],
    mergeColumns: {
      isColumnToMerge: ({ excelTitle }) => {
        return /^Egenskap\s*[1-5]$/i.test(excelTitle);
      },
      mergeValue: ({ excelValue, oldValue }) => {
        if (isBlank(excelValue)) return { value: oldValue };

        const characteristic = additionalCharacteristics.find(item => item.displayName === excelValue);

        if (!characteristic) {
          return { value: oldValue, warnings: [`Ogiltigt värde: "${excelValue}"`] };
        }
        const list = ((oldValue || '') as string).split(',').filter(val => !isBlank(val));
        return { value: [...list, excelValue as string].join(',') };
      },
    },
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Apoteas inköpspris (SEK)',
    useDefaultTitle: true,
    field: 'apoteaPurchasePrice',
    previewOrder: 157,
    previewGroup: { id: 'prices', label: 'Apotea-priser' },
    previewTitle: 'Apoteas inköpspris',
    searchTitleList: ['Apoteas inköpspris (SEK)', 'Apoteas inköpspris i sek'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Listpris (Apotea)',
    useDefaultTitle: true,
    field: 'apoteaListPrice',
    previewOrder: 156,
    previewGroup: { id: 'prices', label: 'Apotea-priser' },
    previewTitle: 'Listpris',
    searchTitleList: ['Listpris (Apotea)', 'Listpris'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Rekommenderat utpris (Apotea, inkl. moms)',
    useDefaultTitle: true,
    field: 'apoteaStorePrice',
    previewOrder: 158,
    previewGroup: { id: 'prices', label: 'Apotea-priser' },
    previewTitle: 'Rekommenderat utpris i SEK (inkl. moms)',
    searchTitleList: ['Rekommenderat utpris (Apotea, inkl. moms)', 'Rekommenderat utpris (inkl. moms)'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Rekommenderat utpris i NOK (inkl. moms)',
    useDefaultTitle: true,
    field: 'apoteaStorePriceNok',
    previewOrder: 159,
    previewGroup: { id: 'prices', label: 'Apotea-priser' },
    unit: 'NOK',
    searchTitleList: ['Rekommenderat utpris i NOK (inkl. moms)', 'Rekommenderat utpris NOK'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Antal konsumentförp. per LAV',
    useDefaultTitle: true,
    field: 'lavQuantity',
    previewOrder: 173,
    previewGroup: { id: 'packQuantities', label: 'Logistik: förpackningsantal' },
    previewTitle: 'Antal konsument-förpackningar per LAV (kfp/lav)',
    searchTitleList: [
      'Antal konsumentförp. per LAV',
      'Antal konsumentförpackningar per LAV',
      'Antal konsument-förpackningar per LAV.',
    ],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'POPs-efterlevnad',
    useDefaultTitle: true,
    field: 'compliancePops',
    previewOrder: 208,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    previewTitle: 'Uppfyller produkten de krav som finns i förordning (EU) 2019/1021 om långlivade organiska föroreningar (POPs)?',
    fieldMappingName: 'POPs-efterlevnad',
    searchTitleList: [
      'POPs-efterlevnad',
      'Uppfyller produkten de krav som finns i förordning (EU) 2019/1021 om långlivade organiska föroreningar (även kallad POPs-förordningen)?',
    ],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Levereras i krympplast (shrinkwrap)',
    useDefaultTitle: true,
    field: 'shrinkWrapPackaging',
    previewOrder: 203,
    previewGroup: { id: 'packaging', label: 'Produktfrågor: förpackning och användning' },
    previewTitle: 'Levereras produkten inplastad, i krympplast eller shrinkwrap?',
    searchTitleList: ['Levereras i krympplast (shrinkwrap)'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
    splitFromColumn: {
      isColumnToSplit: ({ excelTitle }) => {
        return [
          'Levereras produkten i plasttråg eller krympplast/shrinkwrap?',
          'Levereras produkten i plasttråg eller är den inplastad i krympplast/ shrinkwrap?',
        ].includes(excelTitle);
      },
      splitValue: ({ excelValue }) => {
        const booleanValue = convertStringToBoolean(excelValue as string);
        if (booleanValue === true || booleanValue === false) return { value: excelValue };
        return { value: null };
      },
    },
  },
  {
    title: 'CN-kod (varukod, 8 siffror)',
    useDefaultTitle: true,
    field: 'commodityCode',
    previewOrder: 177,
    previewGroup: { id: 'commodity', label: 'Logistik: varukod' },
    previewTitle: 'Produktens varukod (CN-kod, 8 siffror)',
    searchTitleList: ['CN-kod (varukod, 8 siffror)', 'Ange produktens varukod'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Namn på nordisk distributör',
    useDefaultTitle: true,
    field: 'nameNordicDistributor',
    previewOrder: 149,
    previewGroup: { id: 'distributor', label: 'Nordisk distributör' },
    searchTitleList: ['Namn på nordisk distributör'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Innehåller batteri',
    useDefaultTitle: true,
    field: 'containsBattery',
    previewOrder: 207,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    previewTitle: 'Innehåller produkten något batteri?',
    searchTitleList: ['Innehåller batteri', 'Innehåller produkten något batteri?'],
    showInCategories: [
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
    ],
    mandatoryInCategories: [],
  },

  {
    title: 'Uppfyller märkningen kraven i förordning 2013:413',
    isExtra: true,
    useDefaultTitle: true,
    field: 'qrLabelingSwedish',
    previewOrder: 217,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    previewTitle: 'Uppfyller märkningen kraven i den svenska förordningen 2013:413 (inklusive kraven på svensk märkning)?',
    fieldMappingName: 'Uppfyller märkningen kraven i förordning 2013:413',
    searchTitleList: ['Uppfyller märkningen kraven i förordning 2013:413'],
    showInCategories: APOTEA_COSMETIC_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Förpackning och marknadsföring fri från medicinska påståenden',
    isExtra: true,
    useDefaultTitle: true,
    field: 'qrMedicalClaims',
    previewOrder: 218,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    previewTitle: 'Är förpackning och marknadsföring fri från medicinska påståenden?',
    fieldMappingName: 'Förpackning och marknadsföring fri från medicinska påståenden',
    searchTitleList: ['Förpackning och marknadsföring fri från medicinska påståenden'],
    showInCategories: APOTEA_COSMETIC_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Förpackning och marknadsföring fri från cosmetic claims',
    isExtra: true,
    useDefaultTitle: true,
    field: 'qrMarketingClaimsCosmetic',
    previewOrder: 219,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    previewTitle: 'Är förpackning och marknadsföring fri från påståenden (t.ex. "paraben-fri") i enlighet med technical document on cosmetic claims?',
    fieldMappingName: 'Förpackning och marknadsföring fri från cosmetic claims',
    searchTitleList: ['Förpackning och marknadsföring fri från cosmetic claims'],
    showInCategories: APOTEA_COSMETIC_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title:
      'Innehåller kosttillskottet, i rekommenderad daglig dos, vitaminer eller mineralämnen i en daglig dos som överstiger UL-värden (tolerable upper intake levels) enligt Efsa?',
    isExtra: true,
    useDefaultTitle: true,
    field: 'qrEfsaUlValues',
    previewOrder: 226,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    fieldMappingName:
      'Innehåller kosttillskottet, i rekommenderad daglig dos, vitaminer eller mineralämnen i en daglig dos som överstiger UL-värden (tolerable upper intake levels) enligt Efsa?',
    searchTitleList: [
      'Innehåller kosttillskottet, i rekommenderad daglig dos, vitaminer eller mineralämnen i en daglig dos som överstiger UL-värden (tolerable upper intake levels) enligt Efsa?',
    ],
    showInCategories: APOTEA_SUPPLEMENT_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Uppfyller EG 1223/2009 (kosmetikaförordningen)',
    isExtra: true,
    useDefaultTitle: true,
    field: 'complianceEg12232009',
    previewOrder: 188,
    previewGroup: { id: 'cosmeticCompliance', label: 'Kategori: kosmetika och solskydd' },
    previewTitle: 'Överensstämmer produkten med kraven i kosmetikaförordningen (EG) 1223/2009?',
    fieldMappingName: 'Uppfyller EG 1223/2009 (kosmetikaförordningen)',
    searchTitleList: ['Uppfyller EG 1223/2009 (kosmetikaförordningen)'],
    showInCategories: APOTEA_COSMETIC_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Uppfyller solskyddsförordningens påståenden',
    isExtra: true,
    useDefaultTitle: true,
    // Storage-form value (matches the pattern used by containsBattery,
    // complianceBiocid, complianceToyDirective, etc.). PRODUCT_ENUM_FIELD_VALUES
    // accepts both Swedish ("Ej solskyddsmedel") and storage form on input;
    // the default sticks to storage form for consistency with sibling enums.
    defaultValue: 'NO_SUNSCREEN',
    field: 'complianceSunscreenRegulation',
    previewOrder: 189,
    previewGroup: { id: 'cosmeticCompliance', label: 'Kategori: kosmetika och solskydd' },
    previewTitle: 'Uppfyller produkten som är eller innehåller solskyddsmedel de preciserade reglerna?',
    fieldMappingName: 'Uppfyller solskyddsförordningens påståenden',
    searchTitleList: ['Uppfyller solskyddsförordningens påståenden'],
    showInCategories: APOTEA_COSMETIC_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Uppfyller produktens förpackning kraven i PPWR (EU 2025/40)?',
    field: 'compliancePpwrPackaging',
    previewOrder: 206,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    previewTitle: 'Uppfyller produktens förpackning tillämpliga krav i PPWR, Regulation (EU) 2025/40, inklusive krav på dokumentation, märkning, spårbarhet och producentansvar?',
    fieldMappingName: 'PPWR förpackning (EU 2025/40)',
    searchTitleList: [
      'PPWR förpackning (EU 2025/40)',
      'Uppfyller produktens förpackning tillämpliga krav i PPWR, Regulation (EU) 2025/40, inklusive krav på dokumentation, märkning, spårbarhet och producentansvar?',
    ],
    isExtra: true,
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: ALL_GROUP_CATEGORY_IDS,
  },
  {
    title: 'Levereras i plasttråg',
    useDefaultTitle: true,
    field: 'plasticTrayPackaging',
    previewOrder: 202,
    previewGroup: { id: 'packaging', label: 'Produktfrågor: förpackning och användning' },
    previewTitle: 'Levereras produkten i plasttråg?',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
    splitFromColumn: {
      isColumnToSplit: ({ excelTitle }) => {
        return [
          'Levereras produkten i plasttråg eller krympplast/shrinkwrap?',
          'Levereras produkten i plasttråg eller är den inplastad i krympplast/ shrinkwrap?',
        ].includes(excelTitle);
      },
      splitValue: ({ excelValue }) => {
        const booleanValue = convertStringToBoolean(excelValue as string);
        if (booleanValue === true || booleanValue === false) return { value: excelValue };
        return { value: null };
      },
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Apotea Kvalitet-sheet questions — sheets 3.2–3.8 (food / chemical /
  // varor / medicinteknik / foder). Sheets 3.1 + 3.2-supplement (cosmetic
  // + EFSA UL) are defined above via FC-1840. Sheet 3.7 (OTC `Varunummer`)
  // is intentionally omitted — no matching Foodla field exists yet.
  //
  // All 27 quality fields are also registered in `APOTEA_QUALITY_FIELDS`
  // (`column.ts`) — that set drives the mandatory-gating bypass for
  // non-Apotea producers (the columns stay visible via `isExtra: true`
  // but unanswered cells don't block import). Per
  // `docs/apotea/kvalitet-sheets-foodla-alignment-audit.md`.
  // ─────────────────────────────────────────────────────────────────────

  // 3.2 + 3.3 — shared food questions (qrFoodHygiene/Nutrition/Health/Labeling)
  // Apply to all isNewFood categories (incl. Kosttillskott)
  {
    title: 'Är livsmedlet i enlighet med Förordning (EG) 852/2004 om livsmedelshygien?',
    field: 'qrFoodHygiene',
    previewOrder: 214,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    fieldMappingName: 'Livsmedelshygien (EG) 852/2004',
    searchTitleList: [
      'Livsmedelshygien (EG) 852/2004',
      'Är livsmedlet i enlighet med Förordning (EG) nr 852/2004 om livsmedelshygien?',
      'Är kosttillskottet i enlighet med Förordning (EG) nr 852/2004 om livsmedelshygien?',
    ],
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
  },
  {
    title: 'Uppfyller näringspåståenden förordning (EG) 1924/2006?',
    field: 'qrNutritionClaimsCode',
    previewOrder: 223,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    previewTitle: 'Är eventuella näringspåståenden i enlighet med villkoren i förordning (EG) 1924/2006?',
    fieldMappingName: 'Näringspåståenden 1924/2006',
    searchTitleList: [
      'Näringspåståenden 1924/2006',
      'Är eventuella näringspåståenden i enlighet med villkoren i förordning (EG) nr 1924/2006?',
    ],
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
  },
  {
    title: 'Uppfyller hälsopåståenden förordning (EG) 1924/2006?',
    field: 'qrHealthClaimsCode',
    previewOrder: 224,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    previewTitle: 'Är eventuella hälsopåståenden i enlighet med kraven i förordning (EG) 1924/2006?',
    fieldMappingName: 'Hälsopåståenden 1924/2006',
    searchTitleList: [
      'Hälsopåståenden 1924/2006',
      'Är eventuella hälsopåståenden i enlighet med kraven i förordning (EG) nr 1924/2006?',
    ],
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
  },
  {
    title: 'Uppfyller livsmedelsinformationen förordning (EU) 1169/2011?',
    field: 'qrFoodLabeling',
    previewOrder: 225,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    previewTitle: 'Är märkningen i enlighet med förordning (EU) 1169/2011 och Livsmedelsverkets föreskrifter (LIVSFS 2014:4) om livsmedelsinformation?',
    fieldMappingName: 'Livsmedelsinformation 1169/2011',
    searchTitleList: [
      'Livsmedelsinformation 1169/2011',
      'Är märkningen i enlighet med förordning (EU) nr 1169/2011 och Livsmedelsverkets föreskrifter (LIVSFS 2014:4) om livsmedelsinformation?',
    ],
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
  },
  {
    title: 'Bekräfta att säljande uttryck inte är vilseledande',
    field: 'qrMisleadingSalesTerms',
    previewOrder: 222,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    previewTitle: 'Är förpackning och produktbeskrivning fri från säljande uttryck säljande uttryck som kan betraktas som vilseledande?',
    fieldMappingName: 'Vilseledande säljande uttryck',
    searchTitleList: [
      'Vilseledande säljande uttryck',
      'Är förpackning och produktbeskrivning fri från säljande uttryck som kan betraktas som vilseledande?',
    ],
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
  },

  // 3.4 Kvalitet kemiska produkter — isToy ‖ isChemical ‖ isDetergent
  {
    title: 'Uppfyller produkten CLP-förordningen (EG) 1272/2008 om klassificering, märkning och förpackning?',
    field: 'qrClassificationLabelingPackaging',
    previewOrder: 216,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    previewTitle: 'Uppfyller produkten kraven om klassicifering, märkning och förpackning enligt CLP-förordningen (EG) 1272/2008',
    fieldMappingName: 'CLP 1272/2008',
    searchTitleList: [
      'CLP 1272/2008',
      'Uppfyller produkten kraven om klassicifering, märkning och förpackning enligt CLP-förordningen (EG) nr 1272/2008?',
      'Uppfyller produkten kraven om klassificering, märkning och förpackning enligt CLP-förordningen (EG) nr 1272/2008?',
    ],
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
    ],
  },
  {
    title: 'Uppfyller produkten REACH-förordningen (EG) 1907/2006?',
    field: 'complianceReach19072006',
    previewOrder: 191,
    previewGroup: { id: 'reach', label: 'Kategori: REACH' },
    previewTitle: 'Överensstämmer produkten med REACH-förordningen (EG) 1907/2006?',
    fieldMappingName: 'REACH 1907/2006',
    searchTitleList: [
      'REACH 1907/2006',
      'Är produkten i enlighet med Reach-förordningen (EG) nr 1907/2006?',
    ],
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
    ],
  },
  {
    title: 'Uppfyller biocidprodukten kraven i EU:s biocidförordning 528/2012?',
    field: 'complianceBiocid',
    previewOrder: 186,
    previewGroup: { id: 'productCompliance', label: 'Kategori: produktregler' },
    previewTitle: 'Uppfyller varan som är en biocidprodukt kraven i EUs biocidförordning 528/2012?',
    fieldMappingName: 'Biocidprodukt 528/2012',
    searchTitleList: [
      'Biocidprodukt 528/2012',
      'I det fall produkten är en biocidprodukt, uppfyller den kraven i EUs biocidförordning 528/2012?',
      'Uppfyller biocidprodukten kraven i EU:s biocidförordning 528/2012?',
    ],
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
    ],
  },
  {
    title: 'Uppfyller produkten leksaksdirektivet 2009/48/EG (om produkten är leksak)?',
    field: 'complianceToyDirective',
    previewOrder: 185,
    previewGroup: { id: 'productCompliance', label: 'Kategori: produktregler' },
    previewTitle: 'Uppfyller produkten som är en leksak kraven i leksaksdirektivet (EG) 2009/48?',
    fieldMappingName: 'Leksaksdirektivet 2009/48/EG',
    searchTitleList: [
      'Leksaksdirektivet 2009/48/EG',
      'I det fall produkten är en leksak (exempelvis slajm eller fingerfärg), uppfyller den kraven i leksaksdirektivet 2009/48/EG?',
      'I det fall varan är en leksak, uppfyller den kraven i leksaksdirektivet 2009/48/EG?',
    ],
    isExtra: true,
    // Audit gate: isToy ‖ isElectric
    showInCategories: [GROUP_CATEGORY_IDS.LEKSAK, GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [GROUP_CATEGORY_IDS.LEKSAK],
  },
  {
    title: 'Innehåller produkten förbjudna ämnen enligt bilaga I till POPs-förordningen?',
    field: 'containsBannedSubstancesPops',
    previewOrder: 209,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    previewTitle: 'Innehåller produkten något av de förbjudna ämnen som är upptagna i bilaga I i förordning (EU) 2019/1021?',
    fieldMappingName: 'Förbjudna ämnen POPs bilaga I',
    searchTitleList: [
      'Förbjudna ämnen POPs bilaga I',
      'Innehåller produkten något av de förbjudna ämnen som är upptagna i bilaga I i förordning (EU) 2019/1021?',
    ],
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
    ],
  },

  // 3.5 Kvalitet varor — isGoods ‖ isJewelry ‖ isToy ‖ isElectric (mostly)
  {
    title: 'Uppfyller produkten produktsäkerhetslagen 2004:451?',
    field: 'complianceProductSafety2004451',
    previewOrder: 196,
    previewGroup: { id: 'hazardousSubstances', label: 'Kategori: ämnen och produktsäkerhet' },
    previewTitle: 'Följer produkten Produktsäkerhetslagen 2004:451?',
    fieldMappingName: 'Produktsäkerhetslagen 2004:451',
    searchTitleList: [
      'Produktsäkerhetslagen 2004:451',
      'Är varan i enlighet med Produktsäkerhetslagen (2004:451)?',
    ],
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
    ],
  },
  {
    title: 'Uppfyller den biocidbehandlade varan kraven i EU:s biocidförordning 528/2012?',
    field: 'complianceBiocidalTreated',
    previewOrder: 187,
    previewGroup: { id: 'productCompliance', label: 'Kategori: produktregler' },
    previewTitle: 'Uppfyller varan som är biocidbehandlad kraven i EUs biocidförordning 528/2012?',
    fieldMappingName: 'Biocidbehandlad',
    searchTitleList: [
      'Biocidbehandlad',
      'I det fall varan är biocidbehandlad, uppfyller den kraven i EUs biocidförordning 528/2012?',
      'Uppfyller den biocidbehandlade varan kraven i EU:s biocidförordning 528/2012?',
    ],
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
    ],
  },
  {
    title: 'Uppfyller elektrisk produkt RoHS-direktivet (EU/2011/65)?',
    field: 'qrElectronicCode',
    previewOrder: 215,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    previewTitle: 'I det fall varan är en elektrisk eller elektronisk produkt, uppfyller den kraven i RoHS-direktivet EU/2011/65?',
    fieldMappingName: 'RoHS EU/2011/65',
    searchTitleList: [
      'RoHS EU/2011/65',
      'I det fall varan är en elektrisk eller elektronisk produkt, uppfyller den kraven i RoHS-direktivet EU/2011/65?',
    ],
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
    ],
    mandatoryInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
  },
  {
    title: 'Finns svensk bruksanvisning för elektrisk produkt?',
    field: 'confirmGuidanceSwedishElectronics',
    previewOrder: 184,
    previewGroup: { id: 'productCompliance', label: 'Kategori: produktregler' },
    previewTitle: 'Om produkten är elektrisk/elektronisk, finns det svensk bruksanvisning bifogad på förpackningen?',
    fieldMappingName: 'Svensk bruksanvisning elektrisk',
    searchTitleList: [
      'Svensk bruksanvisning elektrisk',
      'I det fall varan är en elektrisk eller elektronisk produkt, finns svensk bruksanvisning bifogad i förpackningen?',
    ],
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
    ],
    mandatoryInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
  },
  {
    title: 'Uppfyller produkten reglerna för tungmetaller vid hud-kontakt (smycken)?',
    field: 'complianceMetalsSkinContact',
    previewOrder: 183,
    previewGroup: { id: 'productCompliance', label: 'Kategori: produktregler' },
    previewTitle: 'Uppfyller produkten reglerna om skadliga tungmetaller (t.ex. nickel, kadmium eller bly) i bilaga XVII till Reach-förordningen?',
    fieldMappingName: 'Tungmetaller hud-kontakt',
    searchTitleList: [
      'Tungmetaller hud-kontakt',
      'I det fall varan är ett smycke, eller på annat sätt kommer i direkt och långvarig kontakt med huden (exempelvis glasögon, klockor, accessoarer, underkläder), uppfyller det reglerna om skadliga tungmetaller (t.ex. nickel, kadmium eller bly) i bilaga XVII till Reach-förordningen?',
    ],
    isExtra: true,
    showInCategories: [GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER],
    mandatoryInCategories: [GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER],
  },

  // 3.6 Kvalitet medicinteknik — isMedical
  {
    title: 'Uppfyller produkten medicintekniska förordningen (EU) 2017/745 eller 2017/746?',
    field: 'complianceMedicalTechProducts',
    previewOrder: 197,
    previewGroup: { id: 'medical', label: 'Kategori: medicinteknik' },
    previewTitle: 'Överensstämmer den medicintekniska produkten kraven i förordningen (EU) 2017/745 eller (EU) 2017/746?',
    fieldMappingName: 'Medicintekniska förordningen 2017/745 / 2017/746',
    searchTitleList: [
      'Medicintekniska förordningen 2017/745 / 2017/746',
      'Uppfyller produkten kraven i förordningen (EU) 2017/745 eller (EU) 2017/746?',
    ],
    isExtra: true,
    showInCategories: [GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER],
    mandatoryInCategories: [GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER],
  },

  // 3.8 Kvalitet foder & -tillskott — isFeedCategory
  {
    title: 'Tillämpas HACCP eller motsvarande egenkontrollssystem i produktionen?',
    field: 'complianceHaccp',
    previewOrder: 181,
    previewGroup: { id: 'feedCompliance', label: 'Kategori: foder' },
    previewTitle: 'Är foder/fodertillskott framställt i enlighet med HACCP?',
    fieldMappingName: 'HACCP egenkontroll',
    searchTitleList: [
      'HACCP egenkontroll',
      'Framställs fodret/fodertillskottet i enlighet med HACCP?',
    ],
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
  },
  {
    title: 'Uppfyller eventuella fodertillsatser förordning (EG) 1831/2003?',
    field: 'qrAdditivesInFeed',
    previewOrder: 220,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    previewTitle: 'Är fodret märkt enligt EU:s förordning om fodertillsatser – ("tillsatsförordningen", förordning (EG) 1831/2003)?',
    fieldMappingName: 'Fodertillsatser 1831/2003',
    searchTitleList: [
      'Fodertillsatser 1831/2003',
      'Är fodret märkt enligt EU:s förordning om fodertillsatser – ("tillsatsförordningen”, förordning (EG) nr 1831/2003)?',
    ],
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
  },
  {
    title: 'Följer produkten FEDIAFs riktlinjer för märkning av sällskapsdjursfoder?',
    field: 'qrFeedLabeling',
    previewOrder: 221,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    previewTitle: 'Är fodret märkt enligt FEDIAF Code of Good Labelling Practice for Pet Food?',
    fieldMappingName: 'FEDIAF märkning',
    searchTitleList: [
      'FEDIAF märkning',
      'Är fodret märkt enligt FEDIAF Code of Good Labelling Practice for Pet Food?',
    ],
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
  },
  {
    title: 'Har produkten relevanta hållbarhetscertifieringar (t.ex. MSC, FSC)?',
    field: 'qrRelevantFeedCertifications',
    previewOrder: 213,
    previewGroup: { id: 'quality', label: 'Ytterligare kvalitetsfrågor' },
    previewTitle: 'Är fodret och/eller emballaget märkt med certifiering inom hållbarhet, t. ex MSC och/eller FSC?',
    fieldMappingName: 'Hållbarhetscertifieringar MSC/FSC',
    searchTitleList: [
      'Hållbarhetscertifieringar MSC/FSC',
      'Är fodret och/eller emballaget märkt med certifiering inom hållbarhet, tex MSC och/eller FSC? Om ja, ange certifikat.',
    ],
    isExtra: true,
    showInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
    mandatoryInCategories: [
      GROUP_CATEGORY_IDS.KOMPLETTERINGSFODER_OCH_FODERTILLSKOTT,
      GROUP_CATEGORY_IDS.FODERRAVARA,
      GROUP_CATEGORY_IDS.HELFODER_OCH_FODERBLANDNINGAR,
    ],
  },
  {
    title: 'Batterimodell',
    subTitle: 'Exempelvis Ultra Power eller Energizer Plus',
    field: 'batteryModel',
    previewOrder: 97,
    previewGroup: { id: 'batteries', label: 'Specifikation — batterier' },
    searchTitleList: ['Batterimodell (Exempelvis Ultra Power eller Energizer Plus.)'],
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Batterityp (Storlek)',
    subTitle: 'Batteriets storlek/typ, t.ex. AAA',
    field: 'batteryType',
    previewOrder: 98,
    previewGroup: { id: 'batteries', label: 'Specifikation — batterier' },
    searchTitleList: ['Batterityp (Storlek) (Batteriets storlek/typ, t.ex. AAA.)'],
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Annan batterityp',
    field: 'batteryTypeOther',
    previewOrder: 99,
    previewGroup: { id: 'batteries', label: 'Specifikation — batterier' },
    searchTitleList: ['Annan batterityp'],
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Spänning',
    subTitle: 'Spänning angiven i volt (V)',
    field: 'batteryVoltage',
    previewOrder: 100,
    previewGroup: { id: 'batteries', label: 'Specifikation — batterier' },
    previewTitle: 'Spänning (V)',
    searchTitleList: ['Spänning (Spänning angiven i volt (V).)'],
    unit: 'V',
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Kemisk sammansättning (Batteriteknik)',
    subTitle: 'Batteriteknik, t.ex. alkaliskt',
    field: 'batteryTechnology',
    previewOrder: 101,
    previewGroup: { id: 'batteries', label: 'Specifikation — batterier' },
    searchTitleList: ['Kemisk sammansättning (Batteriteknik) (Batteriteknik, t.ex. alkaliskt.)'],
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Kapacitet',
    subTitle: 'Batteriets kapacitet i milliamperetimmar (mAh)',
    field: 'batteryCapacity',
    previewOrder: 102,
    previewGroup: { id: 'batteries', label: 'Specifikation — batterier' },
    previewTitle: 'Kapacitet (mAh)',
    searchTitleList: ['Kapacitet (Batteriets kapacitet angiven i milliamperetimmar (mAh). Obligatoriskt för laddningsbara batterier.)'],
    unit: 'mAh',
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Batteritid',
    subTitle: 'Minsta genomsnittliga batteritid i timmar (h)',
    field: 'batteryLife',
    previewOrder: 103,
    previewGroup: { id: 'batteries', label: 'Specifikation — batterier' },
    previewTitle: 'Batteritid (h)',
    searchTitleList: ['Batteritid (Minsta genomsnittliga batteritid angiven i timmar (h), t.ex. 500 h. Obligatoriskt för engångsbatterier.)'],
    unit: 'h',
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Batteriets vikt',
    subTitle: 'Vikt per batteri angivet i gram (g)',
    field: 'batteryWeight',
    previewOrder: 104,
    previewGroup: { id: 'batteries', label: 'Specifikation — batterier' },
    previewTitle: 'Batteriets vikt (g)',
    searchTitleList: ['Batteriets vikt (Vikt per batteri angivet i gram (g).)'],
    unit: 'g',
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Laddningsbar',
    subTitle: 'Är batteriet laddningsbart?',
    field: 'batteryRechargeable',
    previewOrder: 105,
    previewGroup: { id: 'batteries', label: 'Specifikation — batterier' },
    searchTitleList: ['Laddningsbar (Är batteriet laddningsbart? Om Nej visas "Engångsbatteri, ej laddningsbart" i den slutliga produktinformationen.)'],
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Tillverkningsort',
    subTitle: 'Ort för batteritillverkningen, t.ex. Shenzhen',
    field: 'manufacturingLocation',
    previewOrder: 106,
    previewGroup: { id: 'batteries', label: 'Specifikation — batterier' },
    searchTitleList: ["Tillverkningsort (Ort för batteritillverkningsanläggningen, t.ex. 'Shenzhen'.)"],
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Innehåller kvicksilver, kadmium eller bly',
    subTitle: 'Metaller batteriet ska märkas med enligt EU:s batteridirektiv',
    field: 'batteryHazardousMetals',
    previewOrder: 107,
    previewGroup: { id: 'batteries', label: 'Specifikation — batterier' },
    searchTitleList: ['Innehåller kvicksilver, kadmium eller bly (Markera de metaller batteriet ska märkas med. Ett batteri ska märkas med: Hg om det innehåller mer än 0,0005 viktprocent kvicksilver, Cd om mer än 0,002 viktprocent kadmium, Pb om mer än 0,004 viktprocent bly. (EU:s batteridirektiv 2006/66/EG art. 21.))'],
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Effekt angiven i watt.',
    useDefaultTitle: true,
    field: 'lampEffect',
    previewOrder: 108,
    previewGroup: { id: 'lamps', label: 'Specifikation — lampor' },
    previewTitle: 'Effekt (W)',
    fieldMappingName: 'Effekt',
    searchTitleList: ['Effekt'],
    unit: 'W',
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Modellbeteckning för lampan, t.ex. ST26, A60.',
    useDefaultTitle: true,
    field: 'lampModel',
    previewOrder: 109,
    previewGroup: { id: 'lamps', label: 'Specifikation — lampor' },
    previewTitle: 'Lampmodell',
    fieldMappingName: 'Lampmodell',
    searchTitleList: ['Lampmodell'],
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Energimärkningsklass enligt EU-förordning 2019/2015.',
    useDefaultTitle: true,
    field: 'lampEnergyClass',
    previewOrder: 110,
    previewGroup: { id: 'lamps', label: 'Specifikation — lampor' },
    previewTitle: 'Energiklass',
    fieldMappingName: 'Energiklass',
    searchTitleList: ['Energiklass'],
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Lampans teknik, t.ex. LED, halogen, glödtråd.',
    useDefaultTitle: true,
    field: 'lampTechnology',
    previewOrder: 111,
    previewGroup: { id: 'lamps', label: 'Specifikation — lampor' },
    previewTitle: 'Teknik',
    fieldMappingName: 'Teknik',
    searchTitleList: ['Teknik'],
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Lampans sockeltyp, t.ex. E14, E27, GU10.',
    useDefaultTitle: true,
    field: 'lampSocketType',
    previewOrder: 112,
    previewGroup: { id: 'lamps', label: 'Specifikation — lampor' },
    previewTitle: 'Sockeltyp',
    fieldMappingName: 'Sockeltyp',
    searchTitleList: ['Sockeltyp'],
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Kan lampan dimras?',
    useDefaultTitle: true,
    field: 'lampDimmable',
    previewOrder: 113,
    previewGroup: { id: 'lamps', label: 'Specifikation — lampor' },
    previewTitle: 'Dimbar',
    fieldMappingName: 'Dimbar',
    searchTitleList: ['Dimbar'],
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: "Vilken klassisk glödlampa lampans ljuseffekt motsvarar, t.ex. '25W glödlampa'.",
    useDefaultTitle: true,
    field: 'lampEquivalentBulb',
    previewOrder: 114,
    previewGroup: { id: 'lamps', label: 'Specifikation — lampor' },
    previewTitle: 'Motsvarande glödlampa',
    fieldMappingName: 'Motsvarande glödlampa',
    searchTitleList: ['Motsvarande glödlampa'],
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Lampans ljusflöde angivet i lumen (lm).',
    useDefaultTitle: true,
    field: 'lampBrightnessLm',
    previewOrder: 115,
    previewGroup: { id: 'lamps', label: 'Specifikation — lampor' },
    previewTitle: 'Ljusflöde (lm)',
    fieldMappingName: 'Ljusflöde',
    searchTitleList: ['Ljusflöde'],
    unit: 'lm',
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Ljusets färgtemperatur angiven i kelvin (K).',
    useDefaultTitle: true,
    field: 'lampColorTemperatureK',
    previewOrder: 116,
    previewGroup: { id: 'lamps', label: 'Specifikation — lampor' },
    previewTitle: 'Ljusfärg (K)',
    fieldMappingName: 'Ljusfärg',
    searchTitleList: ['Ljusfärg'],
    unit: 'K',
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Genomsnittlig livslängd angiven i timmar (h).',
    useDefaultTitle: true,
    field: 'lampAverageLifespanH',
    previewOrder: 117,
    previewGroup: { id: 'lamps', label: 'Specifikation — lampor' },
    previewTitle: 'Livslängd (h)',
    fieldMappingName: 'Livslängd',
    searchTitleList: ['Livslängd'],
    unit: 'h',
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Färgåtergivningsindex (Ra/CRI).',
    useDefaultTitle: true,
    field: 'lampColorRenderingRa',
    previewOrder: 118,
    previewGroup: { id: 'lamps', label: 'Specifikation — lampor' },
    previewTitle: 'Färgåtergivning (Ra)',
    fieldMappingName: 'Färgåtergivning',
    searchTitleList: ['Färgåtergivning'],
    unit: 'Ra',
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Energiförbrukning för lampan.',
    useDefaultTitle: true,
    field: 'lampEnergyConsumption',
    previewOrder: 119,
    previewGroup: { id: 'lamps', label: 'Specifikation — lampor' },
    previewTitle: 'Energikonsumtion (kWh/1000h)',
    fieldMappingName: 'Energikonsumtion',
    searchTitleList: ['Energikonsumtion'],
    unit: 'kWh/1000h',
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Lampans strålningsvinkel i grader.',
    useDefaultTitle: true,
    field: 'lampBeamAngleDeg',
    previewOrder: 120,
    previewGroup: { id: 'lamps', label: 'Specifikation — lampor' },
    previewTitle: 'Strålningsvinkel (grader)',
    fieldMappingName: 'Strålningsvinkel',
    searchTitleList: ['Strålningsvinkel'],
    unit: '°',
    showInCategories: [GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER],
    mandatoryInCategories: [],
  },
  {
    title: 'Information om hur produkten ska avfallshanteras och återvinnas.',
    useDefaultTitle: true,
    field: 'wasteManagementAndRecyclingInformation',
    previewOrder: 143,
    previewGroup: { id: 'waste', label: 'Avfallshantering' },
    previewTitle: 'Avfallshantering och återvinningsinformation',
    fieldMappingName: 'Avfallshantering och återvinningsinformation',
    searchTitleList: ['Avfallshantering och återvinningsinformation'],
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Brinntid',
    field: 'burningTime',
    previewOrder: 75,
    previewGroup: { id: 'candles', label: 'Ljus' },
    unit: 'timmar',
    showInCategories: [GROUP_CATEGORY_IDS.VAROR],
    mandatoryInCategories: [],
  },
  {
    title: 'Net weight',
    field: 'consumerPackNetWeight',
    unit: 'g',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Transport weight',
    field: 'transportGrossWeight',
    unit: 'g',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Transport depth',
    field: 'transportTradeItemMeasurementsLengthDepth',
    unit: 'millimeter',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Transport width',
    field: 'transportTradeItemMeasurementsWidth',
    unit: 'millimeter',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Transport height',
    field: 'transportTradeItemMeasurementsHeight',
    unit: 'millimeter',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Pallet weight',
    field: 'palletGrossWeight',
    unit: 'g',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Pallet depth',
    field: 'palletTradeItemMeasurementsLengthDepth',
    unit: 'millimeter',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Pallet width',
    field: 'palletTradeItemMeasurementsWidth',
    unit: 'millimeter',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Pallet height',
    field: 'palletTradeItemMeasurementsHeight',
    unit: 'millimeter',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Despatch weight',
    field: 'storePackGrossWeight',
    unit: 'g',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Despatch depth',
    field: 'storePackTradeItemMeasurementsLengthDepth',
    unit: 'millimeter',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Despatch width',
    field: 'storePackTradeItemMeasurementsWidth',
    unit: 'millimeter',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Despatch height',
    field: 'storePackTradeItemMeasurementsHeight',
    unit: 'millimeter',
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },

  {
    title: 'Företagsnamn för konsumentkontakt',
    field: 'consumerContactCompanyName',
    previewOrder: 150,
    previewGroup: { id: 'contact', label: 'Konsumentkontakt' },
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Telefonnummer för konsumentkontakt',
    field: 'consumerContactPhone',
    previewOrder: 151,
    previewGroup: { id: 'contact', label: 'Konsumentkontakt' },
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'E-post för konsumentkontakt',
    field: 'consumerContactEmail',
    previewOrder: 152,
    previewGroup: { id: 'contact', label: 'Konsumentkontakt' },
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Webbplats för konsumentkontakt',
    field: 'consumerContactWebsite',
    previewOrder: 153,
    previewGroup: { id: 'contact', label: 'Konsumentkontakt' },
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Adress för konsumentkontakt',
    field: 'consumerContactAddress',
    previewOrder: 154,
    previewGroup: { id: 'contact', label: 'Konsumentkontakt' },
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Dagsdos',
    field: 'dagsdosAmount',
    previewOrder: 28,
    previewGroup: { id: 'nutrition', label: 'Näringsdeklaration' },
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Dagsdos Enhet',
    field: 'dagsdosUnit',
    previewOrder: 29,
    previewGroup: { id: 'nutrition', label: 'Näringsdeklaration' },
    previewTitle: 'Enhet (dagsdos)',
    showInCategories: [
      GROUP_CATEGORY_IDS.OVRIGT_LIVSMEDEL,
      GROUP_CATEGORY_IDS.FISK_OCH_FISK_PRODUKTER,
      GROUP_CATEGORY_IDS.SPECIAL_LIVSMEDEL_ENLIGT_DEFINITIONEN_I_DIREKTIV_EG_2009_39,
      GROUP_CATEGORY_IDS.DRYCKER,
      GROUP_CATEGORY_IDS.AGG_OCH_AGG_PRODUKTER,
      GROUP_CATEGORY_IDS.KOSTTILLSKOTT,
      GROUP_CATEGORY_IDS.FRUKT_OCH_GRÖNSAKER,
      GROUP_CATEGORY_IDS.KOTT_KRYDDOR_SOPPOR_SASER_SALLADER_OCH_PROTEINPRODUKTER,
      GROUP_CATEGORY_IDS.ALLMANT_LIVSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Övriga ämnen/växtextrakt',
    field: 'other_substances',
    previewOrder: 70,
    previewGroup: { id: 'substances', label: 'Övriga ämnen/växtextrakt' },
    showInCategories: ALL_GROUP_CATEGORY_IDS,
    mandatoryInCategories: [],
  },
  {
    title: 'Faroangivelser',
    field: 'hazardStatements',
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
    ],
    mandatoryInCategories: [],
  },
  {
    title: 'Brandfarlig - underklass',
    field: 'flammableSubtype',
    previewOrder: 138,
    previewGroup: { id: 'hazards', label: 'Faroinformation' },
    previewTitle: 'Brandfarlig – underklass',
    showInCategories: [
      GROUP_CATEGORY_IDS.LEKSAK,
      GROUP_CATEGORY_IDS.ELEKTRISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SMYCKE_KLADER_GLASOGON_ACCESSOARER,
      GROUP_CATEGORY_IDS.VAROR,
      GROUP_CATEGORY_IDS.MEDICINTEKNISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.KEMISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.TVATTMEDEL_DISKMEDEL,
      GROUP_CATEGORY_IDS.KOSMETISKA_PRODUKTER,
      GROUP_CATEGORY_IDS.SOLSKYDDSMEDEL,
    ],
    mandatoryInCategories: [],
  },
];
